import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNotificaciones } from 'contexts/notificaciones.context';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Step, StepIcon, StepIndicator, StepNumber,
  Stepper, StepSeparator, StepStatus, StepTitle, useToast,
} from '@chakra-ui/react';
import JefesViews from './main/salidas/components/JefesViews';
import {
  Box, Button, Flex, SimpleGrid, Icon, Text,
  useColorModeValue, FormControl, FormLabel, Textarea, Grid,
  useColorMode, Badge, Spinner, Center,
  Image, Checkbox, VStack, HStack, Divider,
  Card, CardBody, CardFooter,
  Alert, AlertIcon, AlertDescription, CloseButton, Input, Tooltip,
} from '@chakra-ui/react';
import activosApi from 'api/activos';
import { isSoloLectura } from 'utils/permissions';
import hotelesApi from 'api/hoteles';
import InputField from 'components/fields/InputField';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import Select from 'react-select';
import {
  MdOutlineInventory, MdOutlineTransferWithinAStation,
  MdLaptop, MdTv, MdDevices, MdPhonelink, MdComputer, MdBuild, MdPrint,
  MdArrowBack, MdCancel, MdCheckCircle, MdAccessTime,
  MdDeleteSweep, MdArrowForward, MdSearch,
  MdAdd, MdDelete, MdNotifications, MdPersonAdd, MdWarning,
  MdBlock, MdAssignmentTurnedIn, MdSyncAlt, MdInfo,
  MdPictureAsPdf, MdList, MdPhotoCamera, MdImage, MdClose,
} from 'react-icons/md';
import SignatureCanvas from 'react-signature-canvas';
import { List } from 'react-window';
import { AnimatePresence, motion } from 'framer-motion';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Activo {
  usuarioCreadorSolicitud: any;
  modelo: string;
  folio: string;
  Usuario: string;
  id: number;
  nombre: string;
  numeroSerie: string;
  tipo: string;
  estado: string;
  ubicacion: string;
  URL_Imagen?: string;
  solicitudId?: number;
  salidaId?: number;
  motivoGeneral?: string;
  tipoSalida?: string;
}

interface Hotel { id: string; nombre: string; }

interface FormData {
  Usuario: unknown;
  destino: string;
  motivo: string;
  fechaSalida: string;
  fechaRetorno: string;
  responsable: string;
  observaciones: string;
  folioSalida: string;
  usuarioDestino: string;
  usuarioDestinoId: any;
  tipoSalida: 'temporal' | 'definitiva';
  motivoSeleccionado: string;
  destinatarioTipo: 'interno' | 'externo';
  destinatarioHotel: string;
  contactoExterno: string;
}

interface FirmaST {
  uid: number;
  orden: number;
  nombre: string;
  puesto: string;
  correo: string;
  firma: string;
  firmo: boolean;
}

interface NotificacionPrestamo {
  id: number;
  solicitanteNombre: string;
  cantidad: number;
  fecha: string;
}

interface MotivoActivo { activoId: number; motivo: string; }

interface Firma {
  [x: string]: any;
  usuario_id: any;
  FullName: string;
  orden: any;
  firmo: number;
  firma_base?: string;
  nombre: string;
  puesto: string;
  firma: string;
  canvasRef?: any;
  Id?: number;
}

type VistaActual = 'lista' | 'baja' | 'salida' | 'salida-temporal' | 'transferencia-firmas' | 'ver-salida' | 'ver-salidas-temporales';

// ─── Constantes ──────────────────────────────────────────────────────────────

const MOTIVOS_SALIDA = [
  { value: 'reparacion', label: 'Reparación' },
  { value: 'lavado', label: 'Lavado' },
  { value: 'otro', label: 'Otro' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getActivoIcon = (tipo: string) => {
  switch (tipo.toLowerCase()) {
    case 'laptop': return MdLaptop;
    case 'proyector': return MdTv;
    case 'impresora': return MdPrint;
    case 'tablet': return MdDevices;
    case 'teléfono': return MdPhonelink;
    case 'servidor': return MdComputer;
    default: return MdBuild;
  }
};

const getEstadoColor = (estado: string) => {
  switch (estado) {
    case 'ACTIVO': return 'green';
    case 'ASIGNADO': return 'blue';
    case 'PENDIENTE': return 'yellow';
    case 'PTE. BAJA': return 'orange';
    case 'PTE. SALIDA': return 'purple';
    case 'BODEGA': return 'cyan';
    case 'INACTIVO': return 'red';
    case 'BAJA': return 'red';
    case 'PTE. SALIDA': return 'purple';
    case 'RMA': return 'cyan';
    default: return 'gray';
  }
};

const getDefaultImage = (tipo: string) => {
  switch (tipo.toLowerCase()) {
    case 'laptop': return 'https://via.placeholder.com/40x40/3B82F6/FFFFFF?text=💻';
    case 'proyector': return 'https://via.placeholder.com/40x40/10B981/FFFFFF?text=📽';
    case 'impresora': return 'https://via.placeholder.com/40x40/EF4444/FFFFFF?text=🖨';
    case 'tablet': return 'https://via.placeholder.com/40x40/8B5CF6/FFFFFF?text=📱';
    case 'teléfono': return 'https://via.placeholder.com/40x40/F97316/FFFFFF?text=📞';
    case 'servidor': return 'https://via.placeholder.com/40x40/6B7280/FFFFFF?text=🖥';
    default: return 'https://via.placeholder.com/40x40/9CA3AF/FFFFFF?text=📦';
  }
};

const compressImage = (file: File, maxPx = 1280, quality = 0.75): Promise<string> =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxPx / width, maxPx / height, 1);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });

// ─── Fila virtualizada ───────────────────────────────────────────────────────

interface ActivoRowData {
  activosFiltrados: Activo[];
  selectedSet: Set<number>;
  motivosMap: Map<number, string>;
  onSelect: (id: number) => void;
  onVerBaja: (solicitudId?: number) => void;
  onVerSalida: (solicitudId?: number) => void;
}

const ActivoRow = ({
  ariaAttributes, index, style,
  activosFiltrados, selectedSet, motivosMap, onSelect, onVerBaja, onVerSalida,
}: { ariaAttributes: Record<string, any>; index: number; style: React.CSSProperties } & ActivoRowData) => {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const selectedBg = useColorModeValue('blue.50', 'navy.700');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  const activo = activosFiltrados[index];
  if (!activo) return null;

  const isSelected = selectedSet.has(activo.id);
  const tieneMotivo = isSelected && (motivosMap.get(activo.id) ?? '') !== '';
  const esSalidaPendiente = activo.estado === 'PTE. SALIDA';
  const enProceso = activo.estado === 'PTE. BAJA' || activo.estado === 'BAJA' || esSalidaPendiente;

  return (
    <div style={style} {...ariaAttributes}>
      <Grid
        templateColumns="44px 40px 1fr 80px 1fr 1fr 1fr 90px 100px"
        gap={2} px={6} h="100%"
        alignItems="center"
        borderBottom="1px solid" borderColor={borderColor}
        bg={isSelected ? selectedBg : 'transparent'}
        _hover={{ bg: isSelected ? selectedBg : rowHover }}
        cursor={enProceso ? 'default' : 'pointer'}
        onClick={() => !enProceso && onSelect(activo.id)}
        borderLeft="3px solid"
        borderLeftColor={esSalidaPendiente ? 'purple.400' : enProceso ? 'orange.400' : 'transparent'}
      >
        <Box onClick={e => e.stopPropagation()}>
          {!enProceso && (
            <Checkbox isChecked={isSelected}
              onChange={() => onSelect(activo.id)} colorScheme="blue" />
          )}
        </Box>

        <Box position="relative">
          <Image
            src={activo.URL_Imagen
              ? `https://images.weserv.nl/?url=${activo.URL_Imagen}&w=60&h=60&fit=cover`
              : getDefaultImage(activo.tipo)}
            alt={activo.nombre} w="30px" h="30px" borderRadius="8px" objectFit="cover"
            fallbackSrc={getDefaultImage(activo.tipo)}
          />
          {isSelected && tieneMotivo && (
            <Box position="absolute" top="-2px" right="-2px"
              w="8px" h="8px" bg="green.400" borderRadius="full" border="1.5px solid white" />
          )}
        </Box>

        <Text fontSize="sm" fontWeight="500" color={textPrimary} isTruncated>
          {activo.nombre}
        </Text>

        <Text fontSize="xs" fontWeight="600" color="gray.400" fontFamily="mono">
          {activo.folio}
        </Text>

        <Text fontSize="xs" color={textSecondary} isTruncated>{activo.modelo}</Text>
        <Text fontSize="xs" color={textSecondary} isTruncated fontFamily="mono">{activo.numeroSerie}</Text>
        <Text fontSize="xs" color={textSecondary} isTruncated>{activo.ubicacion}</Text>

        <Flex justify="center">
          <Badge
            colorScheme={getEstadoColor(activo.estado)}
            fontSize="xs" px={2} py={0.5}
            variant={enProceso ? 'solid' : 'subtle'}
            borderRadius="6px"
          >
            {activo.estado}
          </Badge>
        </Flex>

        <Box onClick={e => e.stopPropagation()}>
          {esSalidaPendiente ? (
            <Button size="xs" variant="outline" colorScheme="purple"
              onClick={e => { e.stopPropagation(); onVerSalida(activo.salidaId); }}
              fontSize="xs"
            >
              Ver salida
            </Button>
          ) : enProceso ? (
            <Button size="xs" variant="outline" colorScheme="orange"
              onClick={e => { e.stopPropagation(); onVerBaja(activo.solicitudId); }}
              fontSize="xs"
            >
              Ver baja
            </Button>
          ) : <Box h="24px" />}
        </Box>
      </Grid>
    </div>
  );
};

// ─── Componente ──────────────────────────────────────────────────────────────

const SalidasPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();

  const [usuario, setUsuario] = useState<any>(null);

  useEffect(() => {
    const userData = sessionStorage.getItem('user');
    if (!userData) return;
    try { setUsuario(JSON.parse(userData)); } catch { }
  }, []);

  const fetchFlujosConfig = (hotelAbrev?: string) => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (!user.DepartamentoId) return;
    const hotel = hotelAbrev ?? (sessionStorage.getItem('hotelSeleccionado') || '');
    const localHotelMap: Record<string, number> = {
      GVRN: 1, GVRM: 2, GVBLC: 3, GVLC: 4, VV: 5, CV: 6, MDC: 7, CC: 8, DEMO: 9,
    };
    const hotelId = localHotelMap[hotel];
    if (!hotelId) return;
    const ADMIN_DEPTS = [4, 10];
    const isAdmin = ADMIN_DEPTS.includes(Number(user.DepartamentoId));
    const url = isAdmin
      ? `https://inventariosbackend.velasresorts.com/inventarios/config/flujos/hotel/${hotelId}`
      : `https://inventariosbackend.velasresorts.com/inventarios/config/flujos/hotel/${hotelId}/departamento/${user.DepartamentoId}`;
    fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        let rows: any[] = [];
        if (data.success && data.data) rows = Array.isArray(data.data) ? data.data : [data.data];
        else if (Array.isArray(data)) rows = data;
        const byFlujo: Record<string, any[]> = {};
        rows.forEach((r: any) => {
          const k = r.flujoId ? String(r.flujoId) : `dept-${r.departamentoId}`;
          if (!byFlujo[k]) byFlujo[k] = [];
          byFlujo[k].push(r);
        });
        const flows = Object.entries(byFlujo).map(([key, rows]) => {
          const sorted = [...rows].sort((a: any, b: any) => a.orden - b.orden);
          const first = sorted[0];
          return {
            id: key,
            name: first.nombreFlujo || first.NombreFlujo || `Flujo ${first.NombreDepartamento || key}`,
            tipo: first.tipo || 'ambos',
            departamentoId: Number(first.departamentoId ?? first.DepartamentoId),
            pasos: sorted.map((r: any, i: number) => ({
              uid: i + 1, orden: i + 1,
              nombre: r.NombreUsuario || r.puesto || '',
              puesto: r.NombreUsuario ? (r.puesto || '') : 'EXTERNO',
              correo: r.comentario && r.comentario !== 'N/A' ? r.comentario : (r.Email || ''),
              firma: '', firmo: false,
            })),
          };
        });
        setFlujosConfig(flows);
      })
      .catch(() => {});
  };

  const [vistaActual, setVistaActual] = useState<VistaActual>('lista');
  const [selectedActivos, setSelectedActivos] = useState<number[]>([]);
  const [pasoActual, setPasoActual] = useState(0);
  const [activos, setActivos] = useState<Activo[]>([]);
  const [hoteles, setHoteles] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hotelSeleccionado, setHotelSeleccionado] = useState(
    sessionStorage.getItem('hotelSeleccionado') || ''
  );

  useEffect(() => {
    if (hotelSeleccionado) fetchFlujosConfig(hotelSeleccionado);
  }, [hotelSeleccionado]);

  const sigCanvasRefs = useRef<(SignatureCanvas | null)[]>([]);
  const sigCanvasSTRefs = useRef<(SignatureCanvas | null)[]>([]);
  const firmaST_uidCounter = useRef(10);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [validacionST, setValidacionST] = useState(false);
  const [validacionSalida, setValidacionSalida] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [firmasSalidaView, setFirmasSalidaView] = useState<any[]>([]);
  const [infoSalidaView, setInfoSalidaView] = useState<any>(null);
  const [loadingVerSalida, setLoadingVerSalida] = useState(false);
  const [motivosActivos, setMotivosActivos] = useState<MotivoActivo[]>([]);
  const [mostrarSoloFirmas, setMostrarSoloFirmas] = useState(false);
  const [activosBajaDetalles, setActivosBajaDetalles] = useState<any[]>([]);
  const [loadingFirmas, setLoadingFirmas] = useState(false);

  // ── Flujos configurados ──────────────────────────────────────────────────────
  const [flujosConfig, setFlujosConfig] = useState<{ id: string; name: string; tipo: string; departamentoId: number; pasos: FirmaST[] }[]>([]);
  const [flujoAplicadoId, setFlujoAplicadoId] = useState<string | null>(null);

  // Único flujo aplicable para un departamento en el hotel actualmente seleccionado
  // (flujosConfig ya viene filtrado por hotel desde fetchFlujosConfig).
  const obtenerFlujoDelDepartamento = (deptId: number) =>
    flujosConfig.find(f => Number(f.departamentoId) === Number(deptId));

  // ── Salida Temporal ──────────────────────────────────────────────────────────
  const [pasoSalidaTemporal, setPasoSalidaTemporal] = useState(0);
  const [firmasST, setFirmasST] = useState<FirmaST[]>([]);
  const [idProcesoST, setIdProcesoST] = useState<number | null>(null);
  const [solicitudActiva, setSolicitudActiva] = useState<{ solicitanteNombre: string; cantidad: number } | null>(null);
  const [salidaRegistrada, setSalidaRegistrada] = useState<{ id: number; folio: string } | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [salidasTemporales, setSalidasTemporales] = useState<any[]>([]);
  const [loadingSalidasTemporales, setLoadingSalidasTemporales] = useState(false);
  const { notificaciones, descartar: descartarNotificacion } = useNotificaciones();

  const pasosBaja = ['Selección', 'Motivos', 'Firmas'];
  const pasosSalidaTemporal = ['Datos del Préstamo', 'Activos', 'Firmas'];

  const firmasSTDefault: FirmaST[] = [
    { uid: 1, orden: 1, nombre: '', puesto: 'SOLICITANTE',    correo: '', firma: '', firmo: false },
    { uid: 2, orden: 2, nombre: '', puesto: 'CUSTODIO',       correo: '', firma: '', firmo: false },
    { uid: 3, orden: 3, nombre: '', puesto: 'JEFE INMEDIATO', correo: '', firma: '', firmo: false },
    { uid: 4, orden: 4, nombre: '', puesto: 'CONTRALOR',      correo: '', firma: '', firmo: false },
  ];

  const hotelMap: Record<string, number> = {
    GVRN: 1, GVRM: 2, GVBLC: 3, GVLC: 4, VV: 5, CV: 6, MDC: 7, CC: 8, DEMO: 9,
  };

  // ── Firmas ───────────────────────────────────────────────────────────────────

  const firmasDefault = [
    { nombre: '', puesto: 'SOLICITANTE', firma: '', orden: 1, firmo: 0, FullName: '', usuario_id: '' },
    { nombre: '', puesto: 'JEFE INMEDIATO', firma: '', orden: 2, firmo: 0, FullName: '', usuario_id: '' },
    { nombre: '', puesto: 'CONTRALOR', firma: '', orden: 3, firmo: 0, FullName: '', usuario_id: '' },
    { nombre: '', puesto: 'GERENTE GENERAL', firma: '', orden: 4, firmo: 0, FullName: '', usuario_id: '' },
  ];

  const [firmas, setFirmas] = useState<Firma[]>(firmasDefault.map(f => ({ ...f })));
  const [fotosBaja, setFotosBaja] = useState<{ name: string; preview: string; base64: string }[]>([]);
  const fileInputBajaRef   = useRef<HTMLInputElement>(null);
  const cameraInputBajaRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    destino: '', motivo: '',
    fechaSalida: new Date().toISOString().split('T')[0],
    fechaRetorno: '', responsable: '', observaciones: '', Usuario: '',
    folioSalida: '', usuarioDestino: '', usuarioDestinoId: null,
    tipoSalida: 'temporal', motivoSeleccionado: '',
    destinatarioTipo: 'interno', destinatarioHotel: '', contactoExterno: '',
  });

  // ── Efectos ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!location.state) return;
    const state = location.state as any;
    if (state.fromNotificacion) {
      const { solicitanteNombre, cantidad } = state.fromNotificacion;
      setSolicitudActiva({ solicitanteNombre, cantidad });
      setFormData(prev => ({ ...prev, usuarioDestino: solicitanteNombre }));
    }
    if (state.assetIds) {
      const { actionType, assetIds } = state as { actionType: string; assetIds: number[] };
      if (actionType === 'baja' && assetIds.length > 0) {
        setSelectedActivos(assetIds);
        setTimeout(() => handleAbrirBajaVistaAutomatica(assetIds), 500);
      }
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, activos]);

  useEffect(() => { fetchFirmas(); }, []);
  useEffect(() => {
    if (vistaActual === 'baja' && sessionStorage.getItem('idBajas')) fetchFirmas();
  }, [vistaActual]);

  useEffect(() => {
    const userData = sessionStorage.getItem('user');
    if (!userData) return;
    try {
      const user = JSON.parse(userData);
      setFormData(prev => ({ ...prev, responsable: user.FullName || '' }));
      updateFirma(0, 'nombre', user.FullName || '');
    } catch { }
  }, []);

  useEffect(() => {
    const handle = (e: CustomEvent<{ hotel: string }>) => {
      setHotelSeleccionado(e.detail.hotel);
      cargarDatosIniciales();
      fetchFlujosConfig(e.detail.hotel);
    };
    window.addEventListener('hotelCambiado', handle as EventListener);
    return () => window.removeEventListener('hotelCambiado', handle as EventListener);
  }, []);

  useEffect(() => { cargarDatosIniciales(); }, []);

  useEffect(() => {
    setMotivosActivos(prev => prev.filter(m => selectedActivos.includes(m.activoId)));
  }, [selectedActivos]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const motivosMap = useMemo(
    () => new Map(motivosActivos.map(m => [m.activoId, m.motivo])),
    [motivosActivos]
  );

  const selectedSet = useMemo(() => new Set(selectedActivos), [selectedActivos]);

  // ── API ───────────────────────────────────────────────────────────────────────

  const fetchFirmas = async () => {
    try {
      setLoadingFirmas(true);
      setFirmas(firmasDefault.map(f => ({ ...f, _loading: true })));
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel: 1, usuarioId: user.Id, departamentoId: user.DepartamentoId,
          idProcesoBaja: sessionStorage.getItem('idBajas'),
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setFirmas(
        data.map((f: any, i: number) => ({ ...f, canvasRef: sigCanvasRefs.current[i], _loading: false }))
          .sort((a: any, b: any) => a.orden - b.orden)
      );
    } catch {
      setFirmas(firmasDefault.map(f => ({ ...f })));
    } finally {
      setLoadingFirmas(false);
    }
  };

  const cargarDatosIniciales = async () => {
    try {
      setLoading(true);
      const hotel = sessionStorage.getItem('hotelSeleccionado') || '';
      const hotelId = hotelMap[hotel] || null;
      const activosRes = await activosApi.listadoPorPropiedadSolicitud(hotelId);
      const activosData = activosRes.data || activosRes;
      const hotelesRes = await hotelesApi.listado();
      const hotelesData = hotelesRes.data || hotelesRes;

      setActivos(
        activosData
          .filter((a: any) => !hotelId || a.HotelId === hotelId)
          .map((a: any) => ({
            id: a.Id || a.id,
            nombre: a.ActiveName || a.modelo || 'Sin nombre',
            numeroSerie: a.SerialNumber || 'N/A',
            folio: a.Folio || 'N/A',
            modelo: a.Modelo || 'N/A',
            tipo: a.tipo || 'equipo',
            estado:
              a.Estado === 'active' ? 'ACTIVO' :
              a.Estado === 'assigned' ? 'ASIGNADO' :
              a.Estado === 'pending' ? 'PENDIENTE' :
              a.Estado === 'bodega' ? 'BODEGA' :
              a.Estado === 'pending_baja' ? 'PTE. BAJA' :
              a.Estado === 'pending_salida' ? 'PTE. SALIDA' :
              a.Estado === 'inactive' ? 'INACTIVO' :
              a.Estado === 'baja' ? 'BAJA' :
              a.Estado === 'under_warranty' ? 'EN GARANTÍA' :
              a.Estado === 'in_maintenance' ? 'EN MANTENIMIENTO' :
              a.Estado === 'in_repair' ? 'EN REPARACIÓN' :
              a.Estado === 'rma' ? 'RMA' : 'INACTIVO',
            ubicacion: a.Departamento || 'Sin departamento',
            URL_Imagen: a.URL_Imagen || null,
            HotelId: a.HotelId,
            Usuario: a.Usuario || 'Desconocido',
            solicitudId: a.solicitudId || null,
            salidaId: a.salidaId || null,
            motivoGeneral: a.motivoGeneral,
            tipoSalida: a.tipoSalida || null,
            usuarioCreadorSolicitud: a.usuarioCreadorSolicitud || null,
          }))
      );
      setHoteles(hotelesData.map((h: any) => ({ id: h.Abrev, nombre: h.Name })));
    } catch {
      mostrarError('Error al cargar los activos.');
      setActivos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosDesdeBackend = async (idProcesoBaja: number) => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const hotelId = hotelMap[hotelSeleccionado] || 1;
      const base = { hotel: hotelId, usuarioId: user.Id, departamentoId: user.DepartamentoId };

      const [resFirmas, resActivos] = await Promise.all([
        fetch('https://inventariosbackend.velasresorts.com/inventarios/get-firmas-datas', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...base, idProcesoBaja }),
        }),
        fetch('https://inventariosbackend.velasresorts.com/inventarios/get-activos-baja', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hotel: hotelId, idProcesoBaja }),
        }),
      ]);

      if (!resFirmas.ok || !resActivos.ok) throw new Error();
      const [data, dataActivos] = await Promise.all([resFirmas.json(), resActivos.json()]);

      setActivosBajaDetalles(dataActivos);

      if (Array.isArray(dataActivos) && dataActivos.length > 0) {
        setSelectedActivos(dataActivos.map((i: any) => i.activoId || i.activo_Id));
        setMotivosActivos(
          dataActivos.filter((i: any) => i.activoId || i.activo_Id).map((i: any) => ({
            activoId: i.activoId || i.activo_Id,
            motivo: i.MotivoBaja || i.motivoBaja || i.motivo || '',
          }))
        );
      }

      setFormData(prev => ({
        ...prev,
        motivo: data.motivoGeneral || '',
        observaciones: data.observaciones || '',
        responsable: data.responsable || '',
      }));

      if (data.firmas?.length) {
        setFirmas(
          data.firmas
            .map((f: Firma, i: number) => ({ ...f, canvasRef: sigCanvasRefs.current[i] }))
            .sort((a: Firma, b: Firma) => a.orden - b.orden)
        );
      }

      sessionStorage.setItem('idBajas', idProcesoBaja.toString());
    } catch {
      mostrarError('Error al cargar los datos del proceso de baja');
    }
  };

  // ── Helpers UI ────────────────────────────────────────────────────────────────

  const mostrarError = (msg: string) =>
    toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });

  const mostrarExito = (msg: string) =>
    toast({ title: 'Éxito', description: msg, status: 'success', duration: 5000, isClosable: true });

  const esCorreoValido = (correo: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo.trim());

  const getMotivoActivo = (id: number) => motivosMap.get(id) || '';

  const updateMotivoActivo = (activoId: number, motivo: string) => {
    setMotivosActivos(prev => {
      const existe = prev.find(m => m.activoId === activoId);
      return existe
        ? prev.map(m => m.activoId === activoId ? { ...m, motivo } : m)
        : [...prev, { activoId, motivo }];
    });
  };

  const removeMotivoActivo = (activoId: number) =>
    setMotivosActivos(prev => prev.filter(m => m.activoId !== activoId));

  const updateFirma = (index: number, field: keyof Firma, value: string | number) =>
    setFirmas(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));

  const puedeUsuarioFirmar = (firma: Firma) => {
    if (!usuario) return false;
    const next = firmas.find(f => Number(f.firmo) === 0)?.orden;
    return firma.orden === next && usuario.Id === firma.usuario_id;
  };

  const todasLasFirmasCompletas = () =>
    firmas.every(f => Number(f.firmo) === 1 && f.firma_base?.trim() && f.FullName?.trim());

  const validarProcesoCompleto = () =>
    selectedActivos.length > 0 &&
    motivosActivos.length === selectedActivos.length &&
    motivosActivos.every(m => m.motivo.trim()) &&
    formData.motivo.trim() &&
    firmas.every(f => f.firmo);

  const usuarioCreadorCoincide = () => {
    if (!usuario || !activosBajaDetalles.length) return false;
    const creador = activosBajaDetalles[0]?.usuarioCreadorSolicitud;
    return creador && Number(creador) === Number(usuario.Id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const ESTADOS_PENDIENTES = ['PTE. BAJA', 'BAJA', 'PTE. SALIDA'];

  const activosFiltrados = useMemo(() =>
    activos.filter(a => {
      const pasaBusqueda = !debouncedSearch ||
        [a.nombre, a.numeroSerie, a.folio, a.modelo, a.ubicacion, a.estado]
          .some(v => v.toLowerCase().includes(debouncedSearch.toLowerCase()));
      const pasaFiltro = !soloPendientes || ESTADOS_PENDIENTES.includes(a.estado);
      return pasaBusqueda && pasaFiltro;
    }),
    [activos, debouncedSearch, soloPendientes]
  );

  const selectedActivosObjects = useMemo(() => {
    const set = new Set(selectedActivos);
    return activos.filter(a => set.has(a.id));
  }, [activos, selectedActivos]);

  // ── Handlers de navegación ────────────────────────────────────────────────────

  const scrollToTop = () =>
    setTimeout(() => pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);

  const handleVolverALista = () => {
    setVistaActual('lista');
    setPasoActual(0);
    setPasoSalidaTemporal(0);
    setValidacionST(false);
    setValidacionSalida(false);
    setMostrarSoloFirmas(false);
    setSelectedActivos([]);
    setMotivosActivos([]);
    setFirmasST([]);
    setIdProcesoST(null);
    setSolicitudActiva(null);
    setSalidaRegistrada(null);
    setFotosBaja([]);
    setFirmasSalidaView([]);
    setInfoSalidaView(null);
    sessionStorage.removeItem('idBajas');
    sessionStorage.removeItem('idST');
    setFormData(prev => ({
      ...prev, destino: '', motivo: '', fechaRetorno: '', observaciones: '',
      folioSalida: '', usuarioDestino: '', usuarioDestinoId: null,
      tipoSalida: 'temporal', motivoSeleccionado: '',
      destinatarioTipo: 'interno', destinatarioHotel: '', contactoExterno: '',
    }));
    cargarDatosIniciales();
  };

  const handleSeleccionarActivo = (activoId: number) => {
    const activo = activos.find(a => a.id === activoId);
    if (activo?.estado === 'PTE. BAJA' || activo?.estado === 'BAJA' || activo?.estado === 'PTE. SALIDA') return;
    setSelectedActivos(prev =>
      prev.includes(activoId)
        ? (removeMotivoActivo(activoId), prev.filter(id => id !== activoId))
        : [...prev, activoId]
    );
  };

  const handleSeleccionarTodos = () => {
    const sel = activosFiltrados.filter(a => a.estado !== 'PTE. BAJA' && a.estado !== 'BAJA');
    if (selectedActivos.length === sel.length) {
      setSelectedActivos([]); setMotivosActivos([]);
    } else {
      setSelectedActivos(sel.map(a => a.id));
    }
  };

  // ── Handlers de proceso ───────────────────────────────────────────────────────

  const handleAbrirBajaVistaAutomatica = (ids: number[]) => {
    const deptId = JSON.parse(sessionStorage.getItem('user') || '{}').DepartamentoId;
    const flujoDelDept = obtenerFlujoDelDepartamento(deptId);
    if (!flujoDelDept) {
      mostrarError('No hay creado ningún flujo de firma para este departamento en este hotel.');
      return;
    }
    setSelectedActivos(ids);
    setFlujoAplicadoId(flujoDelDept.id);
    sessionStorage.removeItem('idBajas');
    setPasoActual(0);
    setFotosBaja([]);
    setVistaActual('baja');
    scrollToTop();
  };

  const handleAbrirBajaVista = () => {
    if (!selectedActivos.length) { mostrarError('Seleccione al menos un activo'); return; }
    const deptId = JSON.parse(sessionStorage.getItem('user') || '{}').DepartamentoId;
    const flujoDelDept = obtenerFlujoDelDepartamento(deptId);
    if (!flujoDelDept) {
      mostrarError('No hay creado ningún flujo de firma para este departamento en este hotel.');
      return;
    }
    setFlujoAplicadoId(flujoDelDept.id);
    sessionStorage.removeItem('idBajas');
    setPasoActual(0);
    setFotosBaja([]);
    setVistaActual('baja');
    scrollToTop();
  };

  const handleAbrirSalidaVista = () => {
    if (!selectedActivos.length) { mostrarError('Seleccione al menos un activo'); return; }
    setVistaActual('salida');
  };

  const handleAbrirSalidaTemporalVista = async () => {
    if (!selectedActivos.length) { mostrarError('Seleccione al menos un activo'); return; }
    setValidacionSalida(true);
    if (!formData.usuarioDestino.trim()) { mostrarError('Ingresa el usuario destino'); return; }
    if (formData.destinatarioTipo === 'externo' && !esCorreoValido(formData.contactoExterno)) {
      mostrarError('Ingresa un correo electrónico válido para el destinatario externo'); return;
    }
    if (!formData.fechaRetorno) { mostrarError('Ingresa la fecha de compromiso de devolución'); return; }
    const motivoValido = (formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro') || formData.motivo.trim();
    if (!motivoValido) { mostrarError('Selecciona o ingresa el motivo'); return; }
    setValidacionSalida(false);

    const deptIdST = JSON.parse(sessionStorage.getItem('user') || '{}').DepartamentoId;
    const flujoConFirmantes = obtenerFlujoDelDepartamento(deptIdST);
    if (!flujoConFirmantes) {
      mostrarError('No hay creado ningún flujo de firma para este departamento en este hotel.');
      return;
    }

    const motivoResuelto = formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro'
      ? MOTIVOS_SALIDA.find(m => m.value === formData.motivoSeleccionado)?.label ?? formData.motivo
      : formData.motivo;
    setFormData(prev => ({ ...prev, motivo: motivoResuelto }));
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const hotelId = hotelMap[hotelSeleccionado] || 1;
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/crear-proceso-baja', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel: hotelId, usuarioId: user.Id, departamentoId: user.DepartamentoId,
          activos: selectedActivos, tipoProceso: 'salida_temporal',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.idResultante) {
          setIdProcesoST(data.idResultante);
          sessionStorage.setItem('idST', data.idResultante.toString());
        }
        if (data.folio) setFormData(prev => ({ ...prev, folioSalida: data.folio }));
      }
    } catch { /* sigue aunque falle */ }
    aplicarFlujoConfig(flujoConFirmantes);
    setPasoSalidaTemporal(0);
    setVistaActual('salida-temporal');
    scrollToTop();
  };

  const handleRegistrarSalidaTemporal = async () => {
    const canvas = sigCanvasSTRefs.current[0];
    if (!canvas || canvas.isEmpty()) {
      mostrarError('El solicitante debe firmar antes de registrar'); return;
    }
    if (!firmasST[0]?.nombre.trim()) {
      mostrarError('Ingresa el nombre del solicitante antes de registrar'); return;
    }
    const firmaDataUrl = canvas.toDataURL();
    const firmasConFirma = firmasST.map((f, i) =>
      i === 0 ? { ...f, firma: firmaDataUrl, firmo: true } : f
    );
    const motivoFinal = formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro'
      ? MOTIVOS_SALIDA.find(m => m.value === formData.motivoSeleccionado)?.label ?? formData.motivo
      : formData.motivo;
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const hotelId = hotelMap[hotelSeleccionado] || 1;
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/salida', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activos: selectedActivosObjects.map(a => ({
            id: a.id, nombre: a.nombre, numeroSerie: a.numeroSerie,
            tipo: a.tipo, estado: a.estado, ubicacion: a.ubicacion,
            URL_Imagen: a.URL_Imagen, Usuario: a.Usuario,
          })),
          motivo: motivoFinal,
          fechaSalida: formData.fechaSalida,
          fechaRetorno: formData.fechaRetorno,
          responsable: formData.responsable,
          usuarioDestino: formData.usuarioDestino,
          usuarioDestinoId: formData.usuarioDestinoId,
          folioSalida: formData.folioSalida,
          firmas: firmasConFirma,
          idProcesoST,
          hotel: hotelId,
          usuarioId: user.Id,
          tipoSalida: 'temporal',
          destinatarioTipo: formData.destinatarioTipo,
          destinatarioHotel: formData.destinatarioTipo === 'interno' ? formData.destinatarioHotel : null,
          contactoExterno: formData.destinatarioTipo === 'externo' ? formData.contactoExterno : null,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSalidaRegistrada({ id: data.salidaId, folio: data.folio || formData.folioSalida });
      mostrarExito(`Salida temporal registrada — ${selectedActivos.length} activo(s)`);
    } catch {
      mostrarError('Error al registrar la salida temporal.');
    }
  };

  const handleDescargarPdf = async (salidaId: number, folio: string) => {
    setDescargandoPdf(true);
    try {
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/salidas-temporales/generar-pdf', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salidaId }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pase-salida-${folio}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      mostrarError('Error al generar el PDF. Intenta de nuevo.');
    } finally {
      setDescargandoPdf(false);
    }
  };

  const handleVerSalidasTemporales = async () => {
    setLoadingSalidasTemporales(true);
    setVistaActual('ver-salidas-temporales');
    try {
      const hotelId = hotelMap[hotelSeleccionado] || 1;
      const usuarioId = JSON.parse(sessionStorage.getItem('user') || '{}')?.Id;
      const res = await fetch(`https://inventariosbackend.velasresorts.com/inventarios/salidas-temporales/listado/${hotelId}?usuarioId=${usuarioId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSalidasTemporales(data);
    } catch {
      mostrarError('Error al cargar las salidas temporales.');
      setSalidasTemporales([]);
    } finally {
      setLoadingSalidasTemporales(false);
    }
  };

  const handleGuardarFirmaST = (index: number) => {
    const canvas = sigCanvasSTRefs.current[index];
    if (!canvas || canvas.isEmpty()) { mostrarError('La firma no puede estar vacía'); return; }
    if (!firmasST[index].nombre.trim()) { mostrarError('Ingresa el nombre del firmante'); return; }
    const dataUrl = canvas.toDataURL();
    setFirmasST(prev => prev.map((f, i) => i === index ? { ...f, firma: dataUrl, firmo: true } : f));

    const firma = firmasST[index];
    fetch('https://inventariosbackend.velasresorts.com/inventarios/salidas-temporales/guardar-firma', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firma_base64: dataUrl, nombre: firma.nombre, puesto: firma.puesto,
        correo: firma.correo, orden: firma.orden, idProcesoST, activosId: selectedActivos,
      }),
    }).catch(() => { /* fallo silencioso - firma ya guardada en estado local */ });

    mostrarExito(`Firma de ${firma.puesto} guardada`);
  };

  const handleLimpiarFirmaST = (index: number) => {
    sigCanvasSTRefs.current[index]?.clear();
    setFirmasST(prev => prev.map((f, i) => i === index ? { ...f, firma: '', firmo: false } : f));
  };

  const aplicarFlujoConfig = (flujo: { id: string; pasos: FirmaST[] }) => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const nuevasFirmas: FirmaST[] = flujo.pasos.map((p, i) => ({
      ...p,
      uid: firmaST_uidCounter.current++,
      nombre: i === 0 ? (user.FullName || '') : p.nombre,
      correo: i === 0 ? '' : p.correo,
      firma: '', firmo: false,
    }));
    setFirmasST(nuevasFirmas);
    setFlujoAplicadoId(flujo.id);
  };

  const updateFirmaST = (index: number, field: keyof FirmaST, value: string) =>
    setFirmasST(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));

  const addFirmanteST = () =>
    setFirmasST(prev => [
      ...prev,
      { uid: ++firmaST_uidCounter.current, orden: prev.length + 1, nombre: '', puesto: '', correo: '', firma: '', firmo: false },
    ]);

  const removeFirmanteST = (index: number) => {
    if (firmasST[index].firmo) return;
    setFirmasST(prev => prev.filter((_, i) => i !== index).map((f, i) => ({ ...f, orden: i + 1 })));
    sigCanvasSTRefs.current.splice(index, 1);
  };

  const validarYAvanzarPasoST = () => {
    if (pasoSalidaTemporal === 0) {
      setValidacionST(true);
      if (!formData.usuarioDestino.trim()) { mostrarError('Ingresa el usuario destino'); return; }
      if (!formData.fechaSalida) { mostrarError('Ingresa la fecha de salida'); return; }
      if (!formData.fechaRetorno) { mostrarError('Ingresa la fecha de compromiso de devolución'); return; }
      if (!formData.motivo.trim()) { mostrarError('Ingresa el motivo del préstamo'); return; }
      setValidacionST(false);
    }
    setPasoSalidaTemporal(prev => prev + 1);
    scrollToTop();
  };

  const handleVerBaja = async (activoId: number) => {
    try {
      await cargarDatosDesdeBackend(activoId);
      setPasoActual(2);
      setMostrarSoloFirmas(true);
      setVistaActual('baja');
    } catch {
      mostrarError('Error al cargar el proceso de baja');
    }
  };

  const handleVerSalida = async (idProceso?: number) => {
    console.log('[handleVerSalida] salidaId recibido:', idProceso);
    if (!idProceso) { mostrarError('No se encontró el proceso de salida — salidaId es null, revisa el endpoint de activos'); return; }
    try {
      setLoadingVerSalida(true);
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/get-firmas-salida', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salidaId: idProceso }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const firmasNormalizadas = (data.firmas || [])
        .map((f: any) => ({ ...f, firma_base: f.firma_base64 ?? f.firma_base ?? null }))
        .sort((a: any, b: any) => a.orden - b.orden);
      setFirmasSalidaView(firmasNormalizadas);
      setInfoSalidaView({
        folio: data.folio,
        fechaSalida: data.fechaSalida,
        fechaRetorno: data.fechaRetorno,
        usuarioDestino: data.usuarioDestino,
        motivo: data.motivo,
        tipoSalida: data.tipoSalida,
      });
      scrollToTop();
      setVistaActual('ver-salida');
    } catch {
      mostrarError('No se pudo cargar el proceso. Verifica que el endpoint /get-firmas-salida esté implementado.');
    } finally {
      setLoadingVerSalida(false);
    }
  };

  const handleAbrirFirmasDefinitiva = () => {
    if (!selectedActivos.length) { mostrarError('Seleccione al menos un activo'); return; }
    if (!formData.destino) { mostrarError('Selecciona el hotel destino'); return; }
    const sinMotivo = selectedActivos.filter(id => !getMotivoActivo(id));
    if (sinMotivo.length) { mostrarError(`${sinMotivo.length} activo(s) sin motivo`); return; }
    const motivoValido = (formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro') || formData.motivo.trim();
    if (!motivoValido) { mostrarError('Selecciona o ingresa el motivo'); return; }

    const motivoResuelto = formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro'
      ? MOTIVOS_SALIDA.find(m => m.value === formData.motivoSeleccionado)?.label ?? formData.motivo
      : formData.motivo;
    setFormData(prev => ({ ...prev, motivo: motivoResuelto }));

    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const deptId = user.DepartamentoId;
    const flujoConFirmantes = obtenerFlujoDelDepartamento(deptId);
    if (!flujoConFirmantes) {
      mostrarError('No hay creado ningún flujo de firma para este departamento en este hotel.');
      return;
    }
    aplicarFlujoConfig(flujoConFirmantes);
    setVistaActual('transferencia-firmas');
    scrollToTop();
  };

  const handleRegistrarTransferenciaDefinitiva = async () => {
    const canvas = sigCanvasSTRefs.current[0];
    if (!canvas || canvas.isEmpty()) {
      mostrarError('El solicitante debe firmar antes de registrar'); return;
    }
    if (!firmasST[0]?.nombre.trim()) {
      mostrarError('Ingresa el nombre del solicitante antes de registrar'); return;
    }
    const firmaDataUrl = canvas.toDataURL();
    const firmasConFirma = firmasST.map((f, i) =>
      i === 0 ? { ...f, firma: firmaDataUrl, firmo: true } : f
    );
    try {
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/salida', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activos: selectedActivosObjects.map(a => ({
            id: a.id, nombre: a.nombre, numeroSerie: a.numeroSerie,
            tipo: a.tipo, estado: a.estado, ubicacion: a.ubicacion,
            URL_Imagen: a.URL_Imagen, Usuario: a.Usuario, motivo: getMotivoActivo(a.id),
          })),
          comentario: formData.observaciones,
          destino: formData.destino,
          motivo: formData.motivo,
          fechaSalida: formData.fechaSalida,
          responsable: formData.responsable,
          motivosIndividuales: motivosActivos,
          tipoSalida: 'definitiva',
          firmas: firmasConFirma,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSalidaRegistrada({ id: data.salidaId, folio: data.folio });
      mostrarExito(`Transferencia registrada — ${selectedActivos.length} activo(s)`);
    } catch {
      mostrarError('Error al registrar la transferencia.');
    }
  };

  const guardarMotivosEnSessionStorage = () => {
    try {
      sessionStorage.setItem('motivosEspecificos', JSON.stringify({
        idProcesoBaja: sessionStorage.getItem('idBajas'),
        motivosIndividuales: motivosActivos,
        motivoGeneral: formData.motivo,
        observaciones: formData.observaciones,
        usuarioRegistro: usuario?.Id,
        hotel: hotelSeleccionado,
        selectedActivos,
      }));
    } catch { }
  };

  const validarYAvanzarPaso = async () => {
    if (pasoActual === 0) {
      if (!selectedActivos.length) { mostrarError('Seleccione al menos un activo'); return; }
    }
    if (pasoActual === 1) {
      const sinMotivo = selectedActivos.filter(id => !getMotivoActivo(id));
      if (sinMotivo.length) { mostrarError(`${sinMotivo.length} activo(s) sin motivo`); return; }
      if (!formData.motivo.trim()) { mostrarError('Especifique el motivo general de la baja'); return; }
      if (!fotosBaja.length) { mostrarError('Adjunta al menos una foto como evidencia'); return; }
      guardarMotivosEnSessionStorage();
      // Crear el proceso al llegar a firmas
      try {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const hotelId = hotelMap[hotelSeleccionado] || 1;
        const flujoId = flujoAplicadoId?.startsWith('dept-') ? null : flujoAplicadoId;
        const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/crear-proceso-baja', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hotel: hotelId, usuarioId: user.Id, departamentoId: user.DepartamentoId, activos: selectedActivos, tipoProceso: 'baja_definitiva', flujoId }),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        sessionStorage.setItem('idBajas', data.idResultante);
        await fetchFirmas();
        if (data.folio) mostrarExito(`Proceso iniciado — folio ${data.folio}`);
      } catch {
        mostrarError('No se pudo iniciar el proceso de baja.'); return;
      }
    }
    setPasoActual(pasoActual + 1);
  };

  const handleFotosBajaAdded = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      const base64 = await compressImage(file);
      setFotosBaja(prev => [...prev, { name: file.name, preview: base64, base64 }]);
    }
  };

  const handleEliminarFotoBaja = (index: number) =>
    setFotosBaja(prev => prev.filter((_, i) => i !== index));

  const handleEliminarProcesoBaja = async () => {
    const idProceso = sessionStorage.getItem('idBajas');
    if (!window.confirm('¿Seguro que deseas cancelar este proceso de baja? Los activos volverán a su estado anterior.')) return;
    try {
      if (idProceso) {
        const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/cancelar-proceso-baja', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idProcesoBaja: Number(idProceso) }),
        });
        if (!res.ok) throw new Error();
      }
      mostrarExito('Proceso de baja cancelado');
      handleVolverALista();
    } catch {
      mostrarError('Error al cancelar el proceso. Intenta de nuevo.');
    }
  };

  const handleProcesarBaja = async () => {
    if (!firmas.every(f => Number(f.firmo) === 1)) {
      mostrarError('Todas las firmas deben estar completadas'); return;
    }
    if (!selectedActivos.length) { mostrarError('Sin activos seleccionados'); return; }
    const sinMotivo = selectedActivos.filter(id => !getMotivoActivo(id));
    if (sinMotivo.length) { mostrarError(`${sinMotivo.length} activo(s) sin motivo`); return; }
    if (!window.confirm(`¿Confirma la baja definitiva de ${selectedActivos.length} activo(s)? Esta acción no se puede deshacer.`)) return;

    try {
      const res = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/baja-definitivo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activos: selectedActivosObjects.map(a => ({
            id: a.id, nombre: a.nombre, numeroSerie: a.numeroSerie,
            tipo: a.tipo, estado: a.estado, ubicacion: a.ubicacion,
            URL_Imagen: a.URL_Imagen, Usuario: a.Usuario, motivo: getMotivoActivo(a.id),
          })),
          comentario: formData.observaciones,
          motivo: formData.motivo,
          fechaBaja: new Date().toISOString().split('T')[0],
          responsable: formData.responsable,
          firmas, motivosIndividuales: motivosActivos,
          todasFirmasCompletas: true,
          email: usuario?.Email || '',
          idProcesoBaja: Number(sessionStorage.getItem('idBajas')),
          evidenciaFotos: fotosBaja.map(f => ({ name: f.name, base64: f.base64 })),
        }),
      });
      if (!res.ok) throw new Error();
      mostrarExito(`Baja procesada — ${selectedActivos.length} activo(s)`);
      await fetchFirmas();
      setMotivosActivos([]);
      setFirmas(firmasDefault.map(f => ({ ...f })));
      setFotosBaja([]);
      setVistaActual('lista');
      cargarDatosIniciales();
    } catch {
      mostrarError('Error al procesar la baja.');
    }
  };

  const handleGuardarFirma = (index: number) => {
    const firma = firmas[index];
    if (usuario?.Id !== firma.usuario_id) {
      mostrarError(`No tienes permiso para firmar como ${firma.puesto}.`); return;
    }
    const canvas = sigCanvasRefs.current[index];
    if (!canvas || canvas.isEmpty()) { mostrarError('La firma no puede estar vacía'); return; }

    const dataUrl = canvas.toDataURL();
    updateFirma(index, 'firma', dataUrl);
    updateFirma(index, 'firmo', 1);

    let motivosData: any = null;
    try {
      const stored = sessionStorage.getItem('motivosEspecificos');
      if (stored) motivosData = JSON.parse(stored);
    } catch { }

    fetch('https://inventariosbackend.velasresorts.com/inventarios/test', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        solicito: 'guardar_firma',
        Id: firma.Id,
        firma_base64: dataUrl,
        activosId: selectedActivosObjects.map(a => a.id),
        nombre: firma.nombre, puesto: firma.puesto,
        usuario_id: firma.usuario_id, usuario_actual_id: usuario?.Id,
        ...(index === 0 && motivosData && { motivosEspecificos: motivosData }),
      ...(index === 0 && fotosBaja.length && { evidenciaFotos: fotosBaja.map(f => ({ name: f.name, base64: f.base64 })) }),
      }),
    })
      .then(r => r.json())
      .then(() => {
        mostrarExito(`Firma de ${firma.puesto} guardada`);
        if (index === 0 && motivosData) sessionStorage.removeItem('motivosEspecificos');
      })
      .catch(() => {
        mostrarError('Error al guardar la firma');
        updateFirma(index, 'firma', '');
        updateFirma(index, 'firmo', 0);
      });
  };

  const handleLimpiarFirma = (index: number) => {
    sigCanvasRefs.current[index]?.clear();
    updateFirma(index, 'firma', '');
    updateFirma(index, 'firmo', 0);
  };

  // ── Colores ───────────────────────────────────────────────────────────────────

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const cardBg = useColorModeValue('white', 'navy.800');
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const selectedBg = useColorModeValue('blue.50', 'navy.700');
  const iconBoxBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const inputBorderColor = useColorModeValue('#E2E8F0', 'rgba(255,255,255,0.16)');
  const actionBarBg = useColorModeValue('gray.900', 'gray.950');
  const labelStyle = { fontSize: 'xs' as const, fontWeight: '600' as const, color: textSecondary, textTransform: 'uppercase' as const, letterSpacing: 'widest' as const };

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA LISTA
  // ════════════════════════════════════════════════════════════════════════════

  const renderVistaLista = () => (
    <Box>
      {/* Notificaciones de préstamos pendientes */}
      {notificaciones.length > 0 && (
        <VStack spacing={2} mb={4} align="stretch">
          {notificaciones.map(n => (
            <Alert key={n.id} borderRadius="10px"
              border="1px solid" borderColor="blue.200"
              bg={cardBg} py={3} px={4}
              cursor="pointer"
              onClick={() => {
                setSolicitudActiva({ solicitanteNombre: n.solicitanteNombre, cantidad: n.cantidad });
                setFormData(prev => ({ ...prev, usuarioDestino: n.solicitanteNombre }));
                descartarNotificacion(n.id);
              }}
              _hover={{ borderColor: 'blue.400' }}
              transition="border-color 0.15s"
            >
              <Icon as={MdNotifications} color="blue.400" mr={3} w="18px" h="18px" flexShrink={0} />
              <Box flex={1}>
                <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                  <Text as="span" color="blue.500">{n.solicitanteNombre}</Text>
                  {' '}ha pedido {n.cantidad} equipo(s) en préstamo a ti
                </Text>
                <Text fontSize="xs" color={textSecondary} mt={0.5}>
                  {new Date(n.fecha + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' · '}Haz clic para atender
                </Text>
              </Box>
              <CloseButton
                size="sm" color={textSecondary}
                onClick={e => { e.stopPropagation(); descartarNotificacion(n.id); }}
              />
            </Alert>
          ))}
        </VStack>
      )}

      {/* Banner de solicitud activa */}
      {solicitudActiva && (
        <Box mb={5} p={4} bg="orange.50" border="1px solid" borderColor="orange.200"
          borderRadius="12px" borderLeft="3px solid" borderLeftColor="orange.400">
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              <Icon as={MdAccessTime} color="orange.500" w="18px" h="18px" flexShrink={0} />
              <Box>
                <Text fontSize="sm" fontWeight="700" color="orange.700">
                  Atendiendo solicitud de {solicitudActiva.solicitanteNombre}
                </Text>
                <Text fontSize="xs" color="orange.600" mt={0.5}>
                  Solicita {solicitudActiva.cantidad} equipo(s) en préstamo · Selecciona los activos y presiona &ldquo;Salida temporal&rdquo;
                </Text>
              </Box>
            </HStack>
            <Button size="xs" variant="ghost" colorScheme="orange"
              onClick={() => setSolicitudActiva(null)}>
              Descartar
            </Button>
          </Flex>
        </Box>
      )}

      {/* Stats — sin colores vistosos */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="20px" mb={6}>
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg={iconBoxBg}
              icon={<Icon w="28px" h="28px" as={MdDeleteSweep} color={textSecondary} />} />
          }
          name="Bajas del mes" value={12}
        />
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg={iconBoxBg}
              icon={<Icon w="28px" h="28px" as={MdOutlineTransferWithinAStation} color={textSecondary} />} />
          }
          name="Salidas del mes" value={8}
        />
        <MiniStatistics
          startContent={
            <IconBox w="56px" h="56px" bg={iconBoxBg}
              icon={<Icon w="28px" h="28px" as={MdAccessTime} color={textSecondary} />} />
          }
          name="Salidas temporales" value={5}
        />
      </SimpleGrid>

      {/* Tabla */}
      <Card mb={selectedActivos.length > 0 ? 28 : 6} p="0px" overflow="hidden">
        {/* Header de la tabla */}
        <Box px={6} py={5} borderBottom="1px solid" borderColor={borderColor}>
          <Flex align="center" justify="space-between" mb={4}>
            <Box>
              <Text fontSize="lg" fontWeight="700" color={textPrimary} mb={0.5}>
                Activos disponibles
              </Text>
              <Text {...labelStyle}>
                {hotelSeleccionado || 'Sin hotel'} &nbsp;·&nbsp; {activosFiltrados.length} resultado(s)
              </Text>
            </Box>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="purple"
                leftIcon={<Icon as={MdList} />}
                onClick={handleVerSalidasTemporales}
              >
                Ver salidas
              </Button>
              <motion.div
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.03 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <Button
                  size="sm"
                  variant={soloPendientes ? 'solid' : 'outline'}
                  colorScheme={soloPendientes ? 'orange' : 'gray'}
                  onClick={() => setSoloPendientes(prev => !prev)}
                  leftIcon={<Icon as={MdAccessTime} />}
                >
                  {soloPendientes ? 'Todos' : 'Solo pendientes'}
                </Button>
              </motion.div>
              <AnimatePresence>
                {soloPendientes && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0, transition: { type: 'spring', stiffness: 350, damping: 26 } }}
                    exit={{ opacity: 0, x: -10, transition: { duration: 0.15 } }}
                  >
                    <HStack spacing={1}>
                      <Badge colorScheme="orange" variant="subtle" px={2} py={1} borderRadius="6px">
                        PTE. BAJA: {activos.filter(a => a.estado === 'PTE. BAJA').length}
                      </Badge>
                      <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="6px">
                        PTE. SALIDA: {activos.filter(a => a.estado === 'PTE. SALIDA').length}
                      </Badge>
                    </HStack>
                  </motion.div>
                )}
              </AnimatePresence>
            </HStack>
          </Flex>

          {/* Buscador */}
          <Box position="relative">
            <Icon as={MdSearch} position="absolute" left="12px" top="50%"
              transform="translateY(-50%)" color="gray.400" zIndex={1} w="18px" h="18px" />
            <input
              style={{
                width: '100%',
                padding: '9px 12px 9px 38px',
                border: `1px solid ${inputBorderColor}`,
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: colorMode === 'dark' ? 'transparent' : 'white',
                color: colorMode === 'dark' ? 'white' : '#1A202C',
              }}
              placeholder="Buscar por nombre, folio, serie, departamento..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Box>
        </Box>

        {/* Cabecera de columnas */}
        <Grid
          templateColumns="44px 40px 1fr 80px 1fr 1fr 1fr 90px 100px"
          gap={2} px={6} py={3}
          borderBottom="1px solid" borderColor={borderColor}
        >
          <Box>
            <Checkbox
              isChecked={selectedActivos.length > 0 && selectedActivos.length === activosFiltrados.filter(a => a.estado !== 'PTE. BAJA' && a.estado !== 'BAJA').length}
              isIndeterminate={selectedActivos.length > 0 && selectedActivos.length < activosFiltrados.filter(a => a.estado !== 'PTE. BAJA' && a.estado !== 'BAJA').length}
              onChange={handleSeleccionarTodos} colorScheme="blue"
            />
          </Box>
          <Box />
          {['Nombre', 'Folio', 'Modelo', 'Serie', 'Departamento', 'Estado', 'Acciones'].map(col => (
            <Text key={col} {...labelStyle}>{col}</Text>
          ))}
        </Grid>

        {/* Filas */}
        {loading ? (
          <Center py={14}><Spinner size="lg" color="gray.400" thickness="3px" /></Center>
        ) : activos.length === 0 ? (
          <Center py={14}>
            <VStack spacing={2}>
              <Icon as={MdOutlineInventory} w="40px" h="40px" color="gray.300" />
              <Text color="gray.400" fontSize="sm">No hay activos para este hotel</Text>
            </VStack>
          </Center>
        ) : (
          <List
            style={{ height: 600 }}
            rowCount={activosFiltrados.length}
            rowHeight={56}
            rowComponent={ActivoRow as any}
            rowProps={{
              activosFiltrados,
              selectedSet,
              motivosMap,
              onSelect: handleSeleccionarActivo,
              onVerBaja: handleVerBaja,
              onVerSalida: handleVerSalida,
            }}
          />
        )}
      </Card>

      {/* Barra de acción — oscura, minimalista */}
      <AnimatePresence>
        {selectedActivos.length > 0 && (
        <motion.div
          key="action-bar"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1, transition: { type: 'spring', stiffness: 380, damping: 32 } }}
          exit={{ y: 80, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }}
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}
        >
        <Box
          bg={actionBarBg}
          borderTop="1px solid" borderColor="whiteAlpha.100"
          px={6} py={4}
        >
          <Flex maxW="1400px" mx="auto" align="center" gap={4} wrap="wrap">
            {/* Conteo */}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="600" color="white">
                {selectedActivos.length} activo(s) seleccionado(s)
              </Text>
              <Text fontSize="xs" color="whiteAlpha.500" isTruncated maxW="400px">
                {selectedActivosObjects.map(a => a.folio).join('  ·  ')}
              </Text>
            </Box>

            {/* Acciones */}
            <HStack spacing={2}>
              <Button
                size="sm" variant="ghost" color="whiteAlpha.600"
                _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                onClick={() => { setSelectedActivos([]); setMotivosActivos([]); }}
                leftIcon={<Icon as={MdCancel} />}
              >
                Limpiar
              </Button>

              {!isSoloLectura() && (
                <>
                  <Box w="1px" h="24px" bg="whiteAlpha.200" />

                  <Button
                    size="sm"
                    bg="transparent" color="whiteAlpha.800" border="1px solid" borderColor="whiteAlpha.300"
                    _hover={{ bg: 'whiteAlpha.100', color: 'white', borderColor: 'whiteAlpha.500' }}
                    onClick={handleAbrirSalidaVista}
                    leftIcon={<Icon as={MdOutlineTransferWithinAStation} />}
                  >
                    Salida
                  </Button>

                  <Button
                    size="sm"
                    bg="white" color="gray.900"
                    _hover={{ bg: 'gray.100' }}
                    onClick={handleAbrirBajaVista}
                    leftIcon={<Icon as={MdDeleteSweep} />}
                    fontWeight="600"
                  >
                    Dar de baja
                  </Button>
                </>
              )}
            </HStack>
          </Flex>
        </Box>
        </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // HEADER de formulario (reutilizable)
  // ════════════════════════════════════════════════════════════════════════════

  const renderFormHeader = (titulo: string, subtitulo: string, accentColor: string, extra?: React.ReactNode) => (
    <Box px={6} py={5} borderBottom="1px solid" borderColor={borderColor}>
      <Flex align="center" gap={4}>
        <Button size="sm" variant="ghost" leftIcon={<Icon as={MdArrowBack} />}
          onClick={handleVolverALista} color={textSecondary}
          _hover={{ bg: rowHover }}
        >
          Volver
        </Button>
        <Box w="1px" h="20px" bg={borderColor} />
        <Box w="4px" h="28px" bg={accentColor} borderRadius="full" />
        <Box flex={1}>
          <Text fontSize="md" fontWeight="700" color={textPrimary} lineHeight="1.2">{titulo}</Text>
          <Text fontSize="xs" color={textSecondary} mt={0.5}>{subtitulo}</Text>
        </Box>
        {extra}
      </Flex>
    </Box>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // Tarjetas de activos con motivo
  // ════════════════════════════════════════════════════════════════════════════

  const renderActivosConMotivo = (accentColor: string) => (
    <Box>
      <Text {...labelStyle} mb={3}>Activos — {selectedActivos.length}</Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {selectedActivosObjects.map(activo => {
          const tieneMotivo = getMotivoActivo(activo.id) !== '';
          return (
            <Box key={activo.id} p={4} border="1px solid" borderColor={borderColor}
              borderRadius="12px" bg={cardBg}
              borderLeft="3px solid" borderLeftColor={tieneMotivo ? accentColor : borderColor}
              transition="border-color 0.2s"
            >
              <HStack align="start" mb={3}>
                <Image
                  src={activo.URL_Imagen || getDefaultImage(activo.tipo)}
                  alt={activo.nombre} w="36px" h="36px" borderRadius="8px" objectFit="cover" flexShrink={0}
                />
                <Box flex={1} overflow="hidden">
                  <Text fontWeight="600" fontSize="sm" color={textPrimary} isTruncated>
                    {activo.nombre}
                  </Text>
                  <Text fontSize="xs" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                </Box>
              </HStack>
              <FormControl>
                <FormLabel fontSize="xs" color={textSecondary} mb={1}>Motivo *</FormLabel>
                <Textarea
                  value={getMotivoActivo(activo.id)}
                  onChange={e => updateMotivoActivo(activo.id, e.target.value)}
                  placeholder="Describe el motivo..."
                  size="sm" minH="64px" fontSize="sm" borderRadius="8px"
                  border="1px solid" borderColor={borderColor}
                  _focus={{ borderColor: accentColor, boxShadow: 'none' }}
                  resize="vertical"
                />
              </FormControl>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA SALIDA (Transferencia)
  // ════════════════════════════════════════════════════════════════════════════

  const renderVistaSalida = () => {
    const motivoFinal = formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro'
      ? MOTIVOS_SALIDA.find(m => m.value === formData.motivoSeleccionado)?.label ?? ''
      : formData.motivo;

    const destinoValido = formData.tipoSalida === 'definitiva'
      ? !!formData.destino
      : !!formData.usuarioDestino.trim();

    const contactoExternoValido =
      formData.destinatarioTipo !== 'externo' || esCorreoValido(formData.contactoExterno);

    const puedeRegistrar =
      destinoValido &&
      !!motivoFinal.trim() &&
      contactoExternoValido &&
      (formData.tipoSalida === 'temporal' ? !!formData.fechaRetorno : true) &&
      motivosActivos.length === selectedActivos.length &&
      motivosActivos.every(m => m.motivo.trim());

    const selectStyles = {
      control: (b: any) => ({
        ...b, border: `1px solid ${inputBorderColor}`,
        borderRadius: '10px', fontSize: '14px', minHeight: '40px',
        boxShadow: 'none', '&:hover': { borderColor: '#718096' },
      }),
      option: (b: any, s: any) => ({
        ...b, fontSize: '14px',
        backgroundColor: s.isSelected ? '#2B6CB0' : s.isFocused ? '#EBF8FF' : 'white',
      }),
    };

    return (
      <Card p="0px" overflow="hidden">
        {renderFormHeader(
          'Salida',
          `${selectedActivos.length} activo(s) seleccionado(s)`,
          'blue.400'
        )}

        <CardBody px={6} py={6}>

          {/* Activos seleccionados */}
          <Box mb={5} p={3} border="1px solid" borderColor={borderColor} borderRadius="12px" bg={cardBg}>
            <Text {...labelStyle} mb={2}>Activos seleccionados — {selectedActivos.length}</Text>
            <Flex flexWrap="wrap" gap={2}>
              {selectedActivosObjects.map(activo => (
                <HStack key={activo.id} px={3} py={1.5} border="1px solid" borderColor={borderColor}
                  borderRadius="full" bg={cardBg} spacing={2}>
                  <Image
                    src={activo.URL_Imagen || getDefaultImage(activo.tipo)}
                    alt={activo.nombre} w="18px" h="18px" borderRadius="4px" objectFit="cover" flexShrink={0}
                  />
                  <Text fontSize="xs" fontWeight="600" color={textPrimary}>{activo.nombre}</Text>
                  <Text fontSize="xs" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                </HStack>
              ))}
            </Flex>
          </Box>

          {/* Tipo de salida */}
          <FormControl mb={6}>
            <FormLabel fontSize="sm" fontWeight="600" color={textPrimary}>Tipo de salida *</FormLabel>
            <HStack spacing={3}>
              {[
                { value: 'temporal', label: 'Temporal (préstamo)' },
                { value: 'definitiva', label: 'Definitiva (transferencia)' },
              ].map(opt => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={formData.tipoSalida === opt.value ? 'solid' : 'outline'}
                  colorScheme={formData.tipoSalida === opt.value ? 'blue' : 'gray'}
                  onClick={() => setFormData(prev => ({ ...prev, tipoSalida: opt.value as 'temporal' | 'definitiva' }))}
                >
                  {opt.label}
                </Button>
              ))}
            </HStack>
          </FormControl>

          <AnimatePresence mode="wait">
            <motion.div
              key={formData.tipoSalida}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
              exit={{ opacity: 0, y: -10, transition: { duration: 0.15, ease: 'easeIn' } }}
            >
              {formData.tipoSalida === 'definitiva' && (
                <VStack align="stretch" spacing={2} mb={6}>
                  {[
                    { icon: MdWarning,            color: 'red',    text: 'Esta acción es permanente e irreversible.' },
                    { icon: MdBlock,              color: 'orange', text: 'Este activo dejará de estar disponible en tu inventario.' },
                    { icon: MdAssignmentTurnedIn, color: 'orange', text: 'No podrás crear resguardos ni asignaciones con él.' },
                    { icon: MdSyncAlt,            color: 'blue',   text: 'Aparecerá en el inventario de la propiedad destino.' },
                  ].map(({ icon, color, text }) => (
                    <HStack key={text} px={4} py={2.5} borderRadius="full"
                      bg={`${color}.50`} border="1px solid" borderColor={`${color}.200`}
                      _dark={{ bg: `${color}.900`, borderColor: `${color}.700` }}
                      spacing={3}
                    >
                      <Icon as={icon} color={`${color}.500`} w="15px" h="15px" flexShrink={0} />
                      <Text fontSize="xs" fontWeight="500" color={`${color}.700`} _dark={{ color: `${color}.200` }}>
                        {text}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              )}

              {formData.tipoSalida === 'temporal' && (
                <Flex justify="flex-end" mb={4}>
                  <Tooltip
                    label={
                      <VStack align="start" spacing={1.5} p={1}>
                        <HStack spacing={2}>
                          <Icon as={MdAssignmentTurnedIn} color="blue.300" w="13px" h="13px" flexShrink={0} />
                          <Text fontSize="xs">El destinatario será responsable del activo durante el préstamo.</Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Icon as={MdSyncAlt} color="green.300" w="13px" h="13px" flexShrink={0} />
                          <Text fontSize="xs">Aparecerá en la lista de activos prestados de la persona destino.</Text>
                        </HStack>
                      </VStack>
                    }
                    placement="left"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    borderRadius="10px"
                    px={3} py={2}
                    maxW="280px"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.18, 1], boxShadow: ['0 0 0px 0px rgba(251,146,60,0)', '0 0 0px 7px rgba(251,146,60,0.25)', '0 0 0px 0px rgba(251,146,60,0)'] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                      style={{ display: 'inline-flex', cursor: 'pointer', borderRadius: '50%' }}
                    >
                      <Icon as={MdInfo} w="22px" h="22px" color="orange.400" />
                    </motion.div>
                  </Tooltip>
                </Flex>
              )}

              <Text {...labelStyle} mb={4}>Datos de la salida</Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={5}>
                {formData.tipoSalida === 'definitiva' ? (
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Hotel / Propiedad destino</FormLabel>
                    <Select
                      options={hoteles.map(h => ({ value: h.id, label: h.nombre }))}
                      onChange={(opt: any) => setFormData(prev => ({ ...prev, destino: opt?.value || '' }))}
                      placeholder="Seleccione destino..."
                      styles={selectStyles}
                    />
                  </FormControl>
                ) : (
                  <FormControl isRequired isInvalid={validacionSalida && !formData.usuarioDestino.trim()}>
                    <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Usuario destino *</FormLabel>
                    <Input
                      value={formData.usuarioDestino}
                      onChange={e => setFormData(prev => ({ ...prev, usuarioDestino: e.target.value }))}
                      placeholder="Nombre del destinatario del equipo"
                      size="sm" borderRadius="10px" fontSize="sm"
                      border="1px solid"
                      borderColor={validacionSalida && !formData.usuarioDestino.trim() ? 'red.400' : borderColor}
                      _focus={{ borderColor: 'blue.400', boxShadow: 'none' }}
                    />
                  </FormControl>
                )}

                {formData.tipoSalida === 'temporal' && (<>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Procedencia del destinatario</FormLabel>
                    <HStack spacing={2}>
                      {([
                        { value: 'interno', label: 'Interno' },
                        { value: 'externo', label: 'Externo' },
                      ] as const).map(opt => (
                        <Button
                          key={opt.value}
                          size="sm" flex={1}
                          variant={formData.destinatarioTipo === opt.value ? 'solid' : 'outline'}
                          colorScheme={formData.destinatarioTipo === opt.value ? 'blue' : 'gray'}
                          onClick={() => setFormData(prev => ({ ...prev, destinatarioTipo: opt.value, destinatarioHotel: '', contactoExterno: '' }))}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </HStack>
                  </FormControl>

                  {formData.destinatarioTipo === 'interno' && (
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Hotel del destinatario</FormLabel>
                      <Select
                        options={hoteles.map(h => ({ value: String(h.id), label: h.nombre }))}
                        onChange={(opt: any) => setFormData(prev => ({ ...prev, destinatarioHotel: opt?.label || '' }))}
                        placeholder="Selecciona hotel..."
                        styles={selectStyles}
                      />
                    </FormControl>
                  )}

                  {formData.destinatarioTipo === 'externo' && (
                    <FormControl isRequired isInvalid={validacionSalida && !esCorreoValido(formData.contactoExterno)}>
                      <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Correo del destinatario externo *</FormLabel>
                      <Input
                        value={formData.contactoExterno}
                        onChange={e => setFormData(prev => ({ ...prev, contactoExterno: e.target.value }))}
                        placeholder="correo@ejemplo.com"
                        type="email"
                        size="sm" borderRadius="10px" fontSize="sm"
                        border="1px solid"
                        borderColor={validacionSalida && !esCorreoValido(formData.contactoExterno) ? 'red.400' : borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: 'none' }}
                      />
                    </FormControl>
                  )}
                </>)}

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Fecha de salida</FormLabel>
                  <InputField type="date" name="fechaSalida" value={formData.fechaSalida} onChange={handleInputChange} />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Responsable</FormLabel>
                  <InputField name="responsable" value={formData.responsable} onChange={handleInputChange}
                    placeholder="Nombre del responsable" />
                </FormControl>

                {formData.tipoSalida === 'temporal' && (
                  <FormControl isRequired isInvalid={validacionSalida && !formData.fechaRetorno}>
                    <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Fecha compromiso devolución *</FormLabel>
                    <InputField type="date" name="fechaRetorno" value={formData.fechaRetorno} onChange={handleInputChange} />
                  </FormControl>
                )}
              </SimpleGrid>

              {/* Motivo con opciones predefinidas */}
              {(() => {
                const motivoInvalido = validacionSalida &&
                  !(formData.motivoSeleccionado && formData.motivoSeleccionado !== 'otro') &&
                  !formData.motivo.trim();
                return (
                  <FormControl isRequired mb={5} isInvalid={motivoInvalido}>
                    <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Motivo *</FormLabel>
                    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2} mb={2}>
                      {MOTIVOS_SALIDA.map(m => (
                        <Button
                          key={m.value}
                          size="sm"
                          variant={formData.motivoSeleccionado === m.value ? 'solid' : 'outline'}
                          colorScheme={
                            formData.motivoSeleccionado === m.value ? 'blue'
                            : motivoInvalido ? 'red'
                            : 'gray'
                          }
                          onClick={() => setFormData(prev => ({ ...prev, motivoSeleccionado: m.value }))}
                        >
                          {m.label}
                        </Button>
                      ))}
                    </SimpleGrid>
                    {formData.motivoSeleccionado === 'otro' && (
                      <Textarea
                        name="motivo"
                        value={formData.motivo}
                        onChange={handleInputChange}
                        placeholder="Describe el motivo..."
                        size="sm" minH="80px" borderRadius="10px" fontSize="sm"
                        border="1px solid"
                        borderColor={motivoInvalido && !formData.motivo.trim() ? 'red.400' : borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: 'none' }}
                        mt={1}
                      />
                    )}
                  </FormControl>
                );
              })()}

              <FormControl mb={6}>
                <FormLabel fontSize="sm" fontWeight="500" color={textPrimary}>Observaciones</FormLabel>
                <Textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange}
                  placeholder="Notas adicionales (opcional)"
                  size="sm" minH="64px" borderRadius="10px" fontSize="sm"
                  border="1px solid" borderColor={borderColor}
                  _focus={{ borderColor: 'blue.400', boxShadow: 'none' }} />
              </FormControl>

              {formData.tipoSalida === 'definitiva' && (
                <>
                  <Divider borderColor={borderColor} mb={6} />
                  {renderActivosConMotivo('blue.400')}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </CardBody>

        <CardFooter borderTop="1px solid" borderColor={borderColor} px={6} py={4} justify="space-between">
          <Button variant="ghost" onClick={handleVolverALista} color={textSecondary}
            _hover={{ bg: rowHover }}>
            Cancelar
          </Button>
          <AnimatePresence mode="wait">
            {formData.tipoSalida === 'temporal' ? (
              <motion.div
                key="btn-temporal"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.18, ease: 'easeOut' } }}
                exit={{ opacity: 0, x: 10, transition: { duration: 0.12, ease: 'easeIn' } }}
              >
                <Button colorScheme="orange" onClick={handleAbrirSalidaTemporalVista}
                  rightIcon={<Icon as={MdArrowForward} />}
                >
                  Continuar — Pase de salida
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="btn-definitiva"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.18, ease: 'easeOut' } }}
                exit={{ opacity: 0, x: 10, transition: { duration: 0.12, ease: 'easeIn' } }}
              >
                <Button colorScheme="blue" onClick={handleAbrirFirmasDefinitiva}
                  rightIcon={<Icon as={MdArrowForward} />}
                  isDisabled={!puedeRegistrar}
                >
                  Continuar — Firmas
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </CardFooter>
      </Card>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA SALIDA TEMPORAL
  // ════════════════════════════════════════════════════════════════════════════

  const renderVistaSalidaTemporal = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader(
        'Salida Temporal — Pase de Salida',
        `${selectedActivos.length} activo(s) · retorno con fecha de compromiso`,
        'orange.400'
      )}

      <CardBody px={6} py={6}>

        {/* ── RESUMEN COMPACTO ────────────────────────────────────────────── */}
        <Box mb={5} border="1px solid" borderColor={borderColor} borderRadius="12px" overflow="hidden">
          <SimpleGrid columns={2} borderBottom="1px solid" borderColor={borderColor}>
            {/* Solicitante */}
            <Box px={3} py={2} borderRight="1px solid" borderColor={borderColor}>
              <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Solicitante</Text>
              <Text fontSize="sm" fontWeight="600" color={textPrimary} noOfLines={1}>
                {usuario?.FullName || formData.responsable || '—'}
              </Text>
              {usuario?.Departamento && <Text fontSize="xs" color={textSecondary} noOfLines={1}>{usuario.Departamento}</Text>}
            </Box>

            {/* Destinatario */}
            <Box px={3} py={2}>
              <FormControl isRequired isInvalid={validacionST && !formData.usuarioDestino.trim()}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Destinatario *</Text>
                <Input
                  value={formData.usuarioDestino}
                  onChange={e => setFormData(prev => ({ ...prev, usuarioDestino: e.target.value }))}
                  placeholder="Nombre del destinatario"
                  size="sm" borderRadius="7px" fontSize="sm" border="1px solid"
                  borderColor={validacionST && !formData.usuarioDestino.trim() ? 'red.400' : borderColor}
                  _focus={{ borderColor: 'orange.400', boxShadow: 'none' }}
                />
              </FormControl>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={2} borderBottom="1px solid" borderColor={borderColor}>
            {/* Fecha salida */}
            <Box px={3} py={2} borderRight="1px solid" borderColor={borderColor}>
              <FormControl isRequired isInvalid={validacionST && !formData.fechaSalida}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Fecha de salida *</Text>
                <Input type="date" name="fechaSalida" value={formData.fechaSalida} onChange={handleInputChange}
                  size="sm" borderRadius="7px" fontSize="sm" border="1px solid"
                  borderColor={validacionST && !formData.fechaSalida ? 'red.400' : borderColor}
                  _focus={{ borderColor: 'orange.400', boxShadow: 'none' }} />
              </FormControl>
            </Box>

            {/* Fecha devolución */}
            <Box px={3} py={2} bg="orange.50" _dark={{ bg: 'orange.900' }}>
              <FormControl isRequired isInvalid={validacionST && !formData.fechaRetorno}>
                <Text fontSize="10px" fontWeight="700" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={0.5}>Compromiso devolución *</Text>
                <Input type="date" name="fechaRetorno" value={formData.fechaRetorno} onChange={handleInputChange}
                  size="sm" borderRadius="7px" fontSize="sm" border="1px solid"
                  borderColor={validacionST && !formData.fechaRetorno ? 'red.400' : 'orange.200'}
                  bg="white" _dark={{ bg: 'gray.800' }}
                  _focus={{ borderColor: 'orange.400', boxShadow: 'none' }} />
              </FormControl>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={2}>
            {/* Motivo */}
            <Box px={3} py={2} borderRight="1px solid" borderColor={borderColor}>
              <FormControl isRequired isInvalid={validacionST && !formData.motivo.trim()}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Motivo *</Text>
                <Textarea name="motivo" value={formData.motivo} onChange={handleInputChange}
                  placeholder="¿Por qué sale temporalmente?"
                  size="sm" minH="40px" borderRadius="7px" fontSize="sm" border="1px solid"
                  borderColor={validacionST && !formData.motivo.trim() ? 'red.400' : borderColor}
                  _focus={{ borderColor: 'orange.400', boxShadow: 'none' }} />
              </FormControl>
            </Box>

            {/* Activos */}
            <Box px={3} py={2}>
              <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={1}>
                Activos — {selectedActivos.length}
              </Text>
              <Flex flexWrap="wrap" gap={1.5}>
                {selectedActivosObjects.map(activo => (
                  <HStack key={activo.id} px={2} py={1} border="1px solid" borderColor={borderColor}
                    borderRadius="full" bg={cardBg} spacing={1.5}>
                    <Image
                      src={activo.URL_Imagen || getDefaultImage(activo.tipo)}
                      alt={activo.nombre} w="14px" h="14px" borderRadius="3px" objectFit="cover" flexShrink={0}
                    />
                    <Text fontSize="xs" fontWeight="600" color={textPrimary}>{activo.nombre}</Text>
                    <Text fontSize="10px" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                  </HStack>
                ))}
              </Flex>
            </Box>
          </SimpleGrid>
        </Box>

        {/* ── SELECTOR DE FLUJO ───────────────────────────────────────────── */}
        {flujosConfig.some(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0) && (
          <Box mb={4} p={3} border="1px solid" borderColor={borderColor} borderRadius="10px" bg={cardBg}>
            <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={2}>
              Flujo de firmas
            </Text>
            <Flex flexWrap="wrap" gap={2}>
              {flujosConfig.filter(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0).map(flujo => (
                <Button key={flujo.id} size="xs"
                  variant={flujoAplicadoId === flujo.id ? 'solid' : 'outline'}
                  colorScheme={flujoAplicadoId === flujo.id ? 'orange' : 'gray'}
                  onClick={() => aplicarFlujoConfig(flujo)}
                >
                  {flujo.name} · {flujo.pasos.length} firmantes
                </Button>
              ))}
              <Button size="xs" variant="ghost" colorScheme="gray"
                onClick={() => {
                  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
                  const def = firmasSTDefault.map(f => ({ ...f }));
                  def[0].nombre = user.FullName || '';
                  setFirmasST(def);
                  setFlujoAplicadoId(null);
                }}
              >
                Predeterminado
              </Button>
            </Flex>
          </Box>
        )}

        {/* ── FIRMAS ──────────────────────────────────────────────────────── */}
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text {...labelStyle}>Firmantes del pase de salida</Text>
            <Text fontSize="xs" color={textSecondary} mt={0.5}>
              El solicitante firma aquí. Los demás firmarán posteriormente.
            </Text>
          </Box>
          <Button size="xs" variant="outline" colorScheme="orange"
            leftIcon={<Icon as={MdAdd} />} onClick={addFirmanteST}>
            Agregar firmante
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <AnimatePresence>
            {firmasST.map((firma, index) => (
              <motion.div
                key={firma.uid}
                layout
                initial={{ opacity: 0, scale: 0.93, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 340, damping: 28 } }}
                exit={{ opacity: 0, scale: 0.9, x: -30, transition: { duration: 0.18 } }}
              >
                <Box p={4} border="1px solid" borderColor={borderColor}
                  borderRadius="12px" bg={cardBg}
                  borderLeft="3px solid"
                  borderLeftColor={index === 0 ? 'orange.300' : 'gray.300'}
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Box w="8px" h="8px" borderRadius="full"
                        bg={index === 0 ? 'orange.300' : 'gray.300'} />
                      <Text fontSize="xs" fontWeight="700" color={textSecondary}
                        textTransform="uppercase" letterSpacing="wider">
                        {index === 0 ? 'Solicitante (tú)' : `Firmante ${firma.orden}`}
                      </Text>
                      {index > 0 && (
                        <Badge colorScheme="gray" fontSize="10px" variant="subtle">Firma posterior</Badge>
                      )}
                    </HStack>
                    {index > 0 && (
                      <Button size="xs" variant="ghost" colorScheme="red"
                        onClick={() => removeFirmanteST(index)}
                        leftIcon={<Icon as={MdDelete} w="12px" h="12px" />}>
                        Quitar
                      </Button>
                    )}
                  </Flex>

                  <SimpleGrid columns={2} spacing={2} mb={2}>
                    <FormControl>
                      <FormLabel fontSize="xs" color={textSecondary} mb={1}>Nombre *</FormLabel>
                      <Input
                        value={firma.nombre}
                        onChange={e => updateFirmaST(index, 'nombre', e.target.value)}
                        placeholder="Nombre completo"
                        size="xs" borderRadius="8px" fontSize="xs"
                        border="1px solid" borderColor={borderColor}
                        _focus={{ borderColor: index === 0 ? 'orange.400' : 'gray.400', boxShadow: 'none' }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" color={textSecondary} mb={1}>Puesto</FormLabel>
                      <Input
                        value={firma.puesto}
                        onChange={e => updateFirmaST(index, 'puesto', e.target.value)}
                        placeholder="Cargo / Puesto"
                        size="xs" borderRadius="8px" fontSize="xs"
                        border="1px solid" borderColor={borderColor}
                        _focus={{ borderColor: index === 0 ? 'orange.400' : 'gray.400', boxShadow: 'none' }}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl mb={3}>
                    <FormLabel fontSize="xs" color={textSecondary} mb={1}>
                      Correo electrónico {index > 0 && <Text as="span" color="orange.400">*</Text>}
                    </FormLabel>
                    <Input
                      value={firma.correo}
                      onChange={e => updateFirmaST(index, 'correo', e.target.value)}
                      placeholder={index === 0 ? 'Tu correo (opcional)' : 'correo@empresa.com'}
                      type="email"
                      size="xs" borderRadius="8px" fontSize="xs"
                      border="1px solid" borderColor={borderColor}
                      _focus={{ borderColor: index === 0 ? 'orange.400' : 'gray.400', boxShadow: 'none' }}
                    />
                  </FormControl>

                  {index === 0 ? (
                    <>
                      <Box border="1px solid" borderColor="orange.200"
                        borderRadius="8px" h="100px" bg="white" overflow="hidden">
                        <SignatureCanvas
                          ref={ref => { sigCanvasSTRefs.current[index] = ref; }}
                          penColor="black"
                          canvasProps={{ width: 850, height: 100, className: 'signature-canvas' }}
                        />
                      </Box>
                      <HStack mt={2} justify="flex-end">
                        <Button size="xs" variant="ghost" color={textSecondary}
                          onClick={() => sigCanvasSTRefs.current[index]?.clear()}>
                          Limpiar
                        </Button>
                      </HStack>
                    </>
                  ) : (
                    <Box border="1px dashed" borderColor="gray.200" borderRadius="8px" h="100px"
                      display="flex" alignItems="center" justifyContent="center" bg="gray.50">
                      <VStack spacing={1}>
                        <Icon as={MdPersonAdd} w="20px" h="20px" color="gray.300" />
                        <Text fontSize="xs" color="gray.400" textAlign="center">
                          {firma.nombre ? `Firmará: ${firma.nombre}` : 'Pendiente de firma'}
                        </Text>
                        <Text fontSize="10px" color="gray.300">Se completará posteriormente</Text>
                      </VStack>
                    </Box>
                  )}
                </Box>
              </motion.div>
            ))}
          </AnimatePresence>
        </SimpleGrid>

      </CardBody>

      {salidaRegistrada ? (
        <CardFooter borderTop="1px solid" borderColor="green.200" px={6} py={5}>
          <VStack w="100%" spacing={4}>
            <Box w="100%" p={4} bg="green.50" border="1px solid" borderColor="green.200"
              borderRadius="12px" borderLeft="4px solid" borderLeftColor="green.400"
              _dark={{ bg: 'green.900', borderColor: 'green.700' }}>
              <HStack spacing={3}>
                <Icon as={MdCheckCircle} color="green.500" w="22px" h="22px" flexShrink={0} />
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="700" color="green.700" _dark={{ color: 'green.200' }}>
                    Salida temporal registrada exitosamente
                  </Text>
                  <Text fontSize="xs" color="green.600" _dark={{ color: 'green.300' }} mt={0.5}>
                    Folio: <strong>{salidaRegistrada.folio}</strong> · {selectedActivos.length} activo(s)
                  </Text>
                </Box>
              </HStack>
            </Box>
            <Flex w="100%" justify="space-between" align="center">
              <Button variant="ghost" color={textSecondary} leftIcon={<Icon as={MdArrowBack} />}
                onClick={handleVolverALista} _hover={{ bg: rowHover }}>
                Volver a la lista
              </Button>
              <HStack spacing={2}>
                <Button variant="outline" colorScheme="purple" leftIcon={<Icon as={MdList} />}
                  onClick={handleVerSalidasTemporales}>
                  Ver todas las salidas
                </Button>
                <Button colorScheme="orange" leftIcon={<Icon as={MdPictureAsPdf} />}
                  isLoading={descargandoPdf} loadingText="Generando..."
                  onClick={() => handleDescargarPdf(salidaRegistrada.id, salidaRegistrada.folio)}>
                  Descargar PDF
                </Button>
              </HStack>
            </Flex>
          </VStack>
        </CardFooter>
      ) : (
        <CardFooter borderTop="1px solid" borderColor={borderColor} px={6} py={4}>
          <Flex justify="space-between" align="center" w="100%">
            <Button variant="ghost" color={textSecondary}
              onClick={() => { setValidacionST(false); handleVolverALista(); }}
              leftIcon={<Icon as={MdArrowBack} />}
              _hover={{ bg: rowHover }}
            >
              Volver
            </Button>
            <Button colorScheme="orange" onClick={handleRegistrarSalidaTemporal}
              leftIcon={<Icon as={MdCheckCircle} />}
              isDisabled={isSoloLectura()}
            >
              Registrar salida temporal ({selectedActivos.length})
            </Button>
          </Flex>
        </CardFooter>
      )}
    </Card>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA FIRMAS — TRANSFERENCIA DEFINITIVA
  // ════════════════════════════════════════════════════════════════════════════

  const renderFirmasTransferencia = () => {
    const hotelDestinoNombre = hoteles.find(h => h.id === formData.destino)?.nombre || formData.destino;

    return (
      <Card p="0px" overflow="hidden">
        {renderFormHeader(
          'Transferencia Definitiva — Firmas',
          `${selectedActivos.length} activo(s) → ${hotelDestinoNombre}`,
          'blue.400'
        )}

        <CardBody px={6} py={6}>
          {/* ── RESUMEN COMPACTO ────────────────────────────────────────────── */}
          <Box mb={5} border="1px solid" borderColor={borderColor} borderRadius="12px" overflow="hidden">
            <SimpleGrid columns={2} borderBottom="1px solid" borderColor={borderColor}>
              <Box px={3} py={2} borderRight="1px solid" borderColor={borderColor}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Responsable</Text>
                <Text fontSize="sm" fontWeight="600" color={textPrimary} noOfLines={1}>
                  {usuario?.FullName || formData.responsable || '—'}
                </Text>
              </Box>
              <Box px={3} py={2} bg="blue.50" _dark={{ bg: 'blue.900' }}>
                <Text fontSize="10px" fontWeight="700" color="blue.500" textTransform="uppercase" letterSpacing="wider" mb={0.5}>Hotel destino</Text>
                <Text fontSize="sm" fontWeight="700" color="blue.600" _dark={{ color: 'blue.200' }} noOfLines={1}>
                  {hotelDestinoNombre || '—'}
                </Text>
              </Box>
            </SimpleGrid>
            <SimpleGrid columns={2}>
              <Box px={3} py={2} borderRight="1px solid" borderColor={borderColor}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={0.5}>Motivo</Text>
                <Text fontSize="sm" color={textPrimary} noOfLines={2}>{formData.motivo || '—'}</Text>
              </Box>
              <Box px={3} py={2}>
                <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={1}>
                  Activos — {selectedActivos.length}
                </Text>
                <Flex flexWrap="wrap" gap={1.5}>
                  {selectedActivosObjects.map(activo => (
                    <HStack key={activo.id} px={2} py={1} border="1px solid" borderColor={borderColor}
                      borderRadius="full" bg={cardBg} spacing={1.5}>
                      <Image
                        src={activo.URL_Imagen || getDefaultImage(activo.tipo)}
                        alt={activo.nombre} w="14px" h="14px" borderRadius="3px" objectFit="cover" flexShrink={0}
                      />
                      <Text fontSize="xs" fontWeight="600" color={textPrimary}>{activo.nombre}</Text>
                      <Text fontSize="10px" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                    </HStack>
                  ))}
                </Flex>
              </Box>
            </SimpleGrid>
          </Box>

          {/* ── SELECTOR DE FLUJO ───────────────────────────────────────────── */}
          {flujosConfig.some(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0) && (
            <Box mb={4} p={3} border="1px solid" borderColor={borderColor} borderRadius="10px" bg={cardBg}>
              <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={2}>
                Flujo de firmas
              </Text>
              <Flex flexWrap="wrap" gap={2}>
                {flujosConfig.filter(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0).map(flujo => (
                  <Button key={flujo.id} size="xs"
                    variant={flujoAplicadoId === flujo.id ? 'solid' : 'outline'}
                    colorScheme={flujoAplicadoId === flujo.id ? 'blue' : 'gray'}
                    onClick={() => aplicarFlujoConfig(flujo)}
                  >
                    {flujo.name} · {flujo.pasos.length} firmantes
                  </Button>
                ))}
                <Button size="xs" variant="ghost" colorScheme="gray"
                  onClick={() => {
                    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
                    const def = firmasSTDefault.map(f => ({ ...f }));
                    def[0].nombre = user.FullName || '';
                    setFirmasST(def);
                    setFlujoAplicadoId(null);
                  }}
                >
                  Predeterminado
                </Button>
              </Flex>
            </Box>
          )}

          {/* ── FIRMAS ──────────────────────────────────────────────────────── */}
          <HStack justify="space-between" mb={4}>
            <Box>
              <Text {...labelStyle}>Firmantes de la transferencia</Text>
              <Text fontSize="xs" color={textSecondary} mt={0.5}>
                El solicitante firma aquí. Los demás firmarán posteriormente.
              </Text>
            </Box>
            <Button size="xs" variant="outline" colorScheme="blue"
              leftIcon={<Icon as={MdAdd} />} onClick={addFirmanteST}>
              Agregar firmante
            </Button>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <AnimatePresence>
              {firmasST.map((firma, index) => (
                <motion.div
                  key={firma.uid}
                  layout
                  initial={{ opacity: 0, scale: 0.93, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 340, damping: 28 } }}
                  exit={{ opacity: 0, scale: 0.9, x: -30, transition: { duration: 0.18 } }}
                >
                  <Box p={4} border="1px solid" borderColor={borderColor}
                    borderRadius="12px" bg={cardBg}
                    borderLeft="3px solid"
                    borderLeftColor={index === 0 ? 'blue.300' : 'gray.300'}
                  >
                    <Flex justify="space-between" align="center" mb={3}>
                      <HStack spacing={2}>
                        <Box w="8px" h="8px" borderRadius="full"
                          bg={index === 0 ? 'blue.300' : 'gray.300'} />
                        <Text fontSize="xs" fontWeight="700" color={textSecondary}
                          textTransform="uppercase" letterSpacing="wider">
                          {index === 0 ? 'Solicitante (tú)' : `Firmante ${firma.orden}`}
                        </Text>
                        {index > 0 && (
                          <Badge colorScheme="gray" fontSize="10px" variant="subtle">Firma posterior</Badge>
                        )}
                      </HStack>
                      {index > 0 && (
                        <Button size="xs" variant="ghost" colorScheme="red"
                          onClick={() => removeFirmanteST(index)}
                          leftIcon={<Icon as={MdDelete} w="12px" h="12px" />}>
                          Quitar
                        </Button>
                      )}
                    </Flex>

                    <SimpleGrid columns={2} spacing={2} mb={2}>
                      <FormControl>
                        <FormLabel fontSize="xs" color={textSecondary} mb={1}>Nombre *</FormLabel>
                        <Input
                          value={firma.nombre}
                          onChange={e => updateFirmaST(index, 'nombre', e.target.value)}
                          placeholder="Nombre completo"
                          size="xs" borderRadius="8px" fontSize="xs"
                          border="1px solid" borderColor={borderColor}
                          _focus={{ borderColor: index === 0 ? 'blue.400' : 'gray.400', boxShadow: 'none' }}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="xs" color={textSecondary} mb={1}>Puesto</FormLabel>
                        <Input
                          value={firma.puesto}
                          onChange={e => updateFirmaST(index, 'puesto', e.target.value)}
                          placeholder="Cargo / Puesto"
                          size="xs" borderRadius="8px" fontSize="xs"
                          border="1px solid" borderColor={borderColor}
                          _focus={{ borderColor: index === 0 ? 'blue.400' : 'gray.400', boxShadow: 'none' }}
                        />
                      </FormControl>
                    </SimpleGrid>
                    <FormControl mb={3}>
                      <FormLabel fontSize="xs" color={textSecondary} mb={1}>
                        Correo electrónico {index > 0 && <Text as="span" color="blue.400">*</Text>}
                      </FormLabel>
                      <Input
                        value={firma.correo}
                        onChange={e => updateFirmaST(index, 'correo', e.target.value)}
                        placeholder={index === 0 ? 'Tu correo (opcional)' : 'correo@empresa.com'}
                        type="email"
                        size="xs" borderRadius="8px" fontSize="xs"
                        border="1px solid" borderColor={borderColor}
                        _focus={{ borderColor: index === 0 ? 'blue.400' : 'gray.400', boxShadow: 'none' }}
                      />
                    </FormControl>

                    {index === 0 ? (
                      <>
                        <Box border="1px solid" borderColor="blue.200"
                          borderRadius="8px" h="100px" bg="white" overflow="hidden">
                          <SignatureCanvas
                            ref={ref => { sigCanvasSTRefs.current[index] = ref; }}
                            penColor="black"
                            canvasProps={{ width: 850, height: 100, className: 'signature-canvas' }}
                          />
                        </Box>
                        <HStack mt={2} justify="flex-end">
                          <Button size="xs" variant="ghost" color={textSecondary}
                            onClick={() => sigCanvasSTRefs.current[index]?.clear()}>
                            Limpiar
                          </Button>
                        </HStack>
                      </>
                    ) : (
                      <Box border="1px dashed" borderColor="gray.200" borderRadius="8px" h="100px"
                        display="flex" alignItems="center" justifyContent="center" bg="gray.50">
                        <VStack spacing={1}>
                          <Icon as={MdPersonAdd} w="20px" h="20px" color="gray.300" />
                          <Text fontSize="xs" color="gray.400" textAlign="center">
                            {firma.nombre ? `Firmará: ${firma.nombre}` : 'Pendiente de firma'}
                          </Text>
                          <Text fontSize="10px" color="gray.300">Se completará posteriormente</Text>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                </motion.div>
              ))}
            </AnimatePresence>
          </SimpleGrid>
        </CardBody>

        {salidaRegistrada ? (
          <CardFooter borderTop="1px solid" borderColor="green.200" px={6} py={5}>
            <VStack w="100%" spacing={4}>
              <Box w="100%" p={4} bg="green.50" border="1px solid" borderColor="green.200"
                borderRadius="12px" borderLeft="4px solid" borderLeftColor="green.400"
                _dark={{ bg: 'green.900', borderColor: 'green.700' }}>
                <HStack spacing={3}>
                  <Icon as={MdCheckCircle} color="green.500" w="22px" h="22px" flexShrink={0} />
                  <Box flex={1}>
                    <Text fontSize="sm" fontWeight="700" color="green.700" _dark={{ color: 'green.200' }}>
                      Transferencia registrada — pendiente de firmas y confirmación en portería
                    </Text>
                    <Text fontSize="xs" color="green.600" _dark={{ color: 'green.300' }} mt={0.5}>
                      Folio: <strong>{salidaRegistrada.folio}</strong> · {selectedActivos.length} activo(s) · destino: {hotelDestinoNombre}
                    </Text>
                  </Box>
                </HStack>
              </Box>
              <Flex w="100%" justify="flex-end">
                <Button variant="ghost" color={textSecondary} leftIcon={<Icon as={MdArrowBack} />}
                  onClick={handleVolverALista} _hover={{ bg: rowHover }}>
                  Volver a la lista
                </Button>
              </Flex>
            </VStack>
          </CardFooter>
        ) : (
          <CardFooter borderTop="1px solid" borderColor={borderColor} px={6} py={4}>
            <Flex justify="space-between" align="center" w="100%">
              <Button variant="ghost" color={textSecondary}
                onClick={() => setVistaActual('salida')}
                leftIcon={<Icon as={MdArrowBack} />}
                _hover={{ bg: rowHover }}
              >
                Volver
              </Button>
              <Button colorScheme="blue" onClick={handleRegistrarTransferenciaDefinitiva}
                leftIcon={<Icon as={MdCheckCircle} />}
                isDisabled={isSoloLectura()}
              >
                Registrar transferencia ({selectedActivos.length})
              </Button>
            </Flex>
          </CardFooter>
        )}
      </Card>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER FIRMAS
  // ════════════════════════════════════════════════════════════════════════════

  const renderFirmas = () => {
    if (loadingFirmas) {
      return (
        <Center py={10}>
          <VStack spacing={3}>
            <Spinner size="md" color="gray.400" thickness="3px" />
            <Text fontSize="sm" color={textSecondary}>Cargando firmantes...</Text>
          </VStack>
        </Center>
      );
    }

    return (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {firmas.sort((a, b) => a.orden - b.orden).map((firma, index) => {
          const firmado = Number(firma.firmo) === 1;
          const puedeFirmar = puedeUsuarioFirmar(firma);
          const esUsuario = usuario?.Id === firma.usuario_id;

          if (firma._loading) {
            return (
              <Box key={firma.orden} p={4} border="1px solid" borderColor={borderColor} borderRadius="12px">
                <Center h="130px"><Spinner size="sm" color="gray.300" /></Center>
              </Box>
            );
          }

          return (
            <Box
              key={firma.orden}
              p={4}
              border="1px solid"
              borderColor={firmado ? 'green.200' : puedeFirmar ? borderColor : borderColor}
              borderRadius="12px"
              bg={cardBg}
              borderLeft="3px solid"
              borderLeftColor={firmado ? 'green.400' : puedeFirmar ? 'blue.400' : 'gray.200'}
              opacity={!firmado && !puedeFirmar ? 0.6 : 1}
              transition="all 0.15s"
            >
              <Flex justify="space-between" align="start" mb={3}>
                <Box>
                  <HStack spacing={2} mb={0.5}>
                    <Text fontSize="sm" fontWeight="600" color={textPrimary}>{firma.puesto}</Text>
                    {puedeFirmar && (
                      <Badge colorScheme="blue" fontSize="10px" variant="subtle">Tu turno</Badge>
                    )}
                  </HStack>
                  {firma.FullName && (
                    <Text fontSize="xs" color={textSecondary}>{firma.FullName}</Text>
                  )}
                </Box>
                <Box
                  w="8px" h="8px" borderRadius="full"
                  bg={firmado ? 'green.400' : 'gray.300'} mt={1}
                />
              </Flex>

              {/* Área de firma */}
              <Box
                border="1px solid" borderColor={puedeFirmar ? 'blue.200' : borderColor}
                borderRadius="8px" h="110px" bg="white" overflow="hidden"
                pointerEvents={puedeFirmar ? 'auto' : 'none'}
              >
                {firma.firma_base ? (
                  <Image src={firma.firma_base} alt={`Firma ${firma.puesto}`}
                    objectFit="contain" w="100%" h="110px" />
                ) : (
                  <SignatureCanvas
                    ref={ref => { sigCanvasRefs.current[index] = ref; }}
                    penColor="black"
                    canvasProps={{ width: 850, height: 110, className: 'signature-canvas' }}
                  />
                )}
              </Box>

              {/* Mensaje de espera */}
              {!puedeFirmar && esUsuario && !firmado && (
                <Text fontSize="xs" color={textSecondary} mt={2}>
                  Espera tu turno para firmar.
                </Text>
              )}

              {/* Botones */}
              {puedeFirmar && !firma.firma_base && (
                <HStack mt={2} spacing={2} justify="flex-end">
                  <Button size="xs" variant="ghost" onClick={() => handleLimpiarFirma(index)}
                    color={textSecondary}>
                    Limpiar
                  </Button>
                  <Button size="xs" colorScheme="blue" onClick={() => handleGuardarFirma(index)}>
                    Guardar firma
                  </Button>
                </HStack>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA VER SALIDA (progreso de firmas)
  // ════════════════════════════════════════════════════════════════════════════

  const renderVerSalida = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader(
        'Progreso de salida',
        infoSalidaView?.tipoSalida === 'temporal' ? 'Salida temporal — firmas' : 'Salida definitiva — firmas',
        'purple.400'
      )}

      <CardBody px={6} py={6}>
        {loadingVerSalida ? (
          <Center py={14}><Spinner size="lg" color="purple.400" thickness="3px" /></Center>
        ) : (
          <>
            {/* Resumen del proceso */}
            {infoSalidaView && (
              <Box mb={6} p={4} border="1px solid" borderColor={borderColor}
                borderRadius="12px" borderLeft="3px solid" borderLeftColor="purple.400">
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {infoSalidaView.folio && (
                    <Box>
                      <Text {...labelStyle}>Folio</Text>
                      <Text fontSize="sm" fontWeight="700" color="purple.500">{infoSalidaView.folio}</Text>
                    </Box>
                  )}
                  <Box>
                    <Text {...labelStyle}>Tipo</Text>
                    <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                      {infoSalidaView.tipoSalida === 'temporal' ? 'Temporal' : 'Definitiva'}
                    </Text>
                  </Box>
                  {infoSalidaView.usuarioDestino && (
                    <Box>
                      <Text {...labelStyle}>Destinatario</Text>
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>{infoSalidaView.usuarioDestino}</Text>
                    </Box>
                  )}
                  {infoSalidaView.fechaSalida && (
                    <Box>
                      <Text {...labelStyle}>Fecha salida</Text>
                      <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                        {new Date(infoSalidaView.fechaSalida).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}
                      </Text>
                    </Box>
                  )}
                  {infoSalidaView.fechaRetorno && (
                    <Box>
                      <Text {...labelStyle}>Compromiso devolución</Text>
                      <motion.div
                        animate={{ scale: [1, 1.04, 1], opacity: [1, 0.75, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                        style={{ display: 'inline-block' }}
                      >
                        <Text fontSize="sm" fontWeight="700" color="orange.500"
                          px={2} py={0.5} borderRadius="6px"
                          bg="orange.50" border="1px solid" borderColor="orange.200"
                          _dark={{ bg: 'orange.900', borderColor: 'orange.700' }}
                        >
                          {new Date(infoSalidaView.fechaRetorno).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}
                        </Text>
                      </motion.div>
                    </Box>
                  )}
                  {infoSalidaView.motivo && (
                    <Box gridColumn={{ md: 'span 2' }}>
                      <Text {...labelStyle}>Motivo</Text>
                      <Text fontSize="sm" color={textPrimary} noOfLines={2}>{infoSalidaView.motivo}</Text>
                    </Box>
                  )}
                </SimpleGrid>
              </Box>
            )}

            {/* Progreso visual de firmas */}
            <Text {...labelStyle} mb={4}>
              Firmas — {firmasSalidaView.filter(f => Number(f.firmo) === 1).length}/{firmasSalidaView.length} completadas
            </Text>

            {/* Barra de progreso */}
            <Box mb={5} h="6px" bg={borderColor} borderRadius="full" overflow="hidden">
              <Box
                h="100%"
                bg="purple.400"
                borderRadius="full"
                w={`${firmasSalidaView.length > 0 ? (firmasSalidaView.filter(f => Number(f.firmo) === 1).length / firmasSalidaView.length) * 100 : 0}%`}
                transition="width 0.4s ease"
              />
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {firmasSalidaView.map((firma, idx) => {
                const firmado = Number(firma.firmo) === 1;
                return (
                  <Box key={idx} p={4} border="1px solid"
                    borderColor={firmado ? 'green.200' : borderColor}
                    borderRadius="12px" bg={cardBg}
                    borderLeft="3px solid"
                    borderLeftColor={firmado ? 'green.400' : 'gray.200'}
                    transition="all 0.15s"
                  >
                    <Flex justify="space-between" align="start" mb={2}>
                      <Box>
                        <HStack spacing={2} mb={0.5}>
                          <Text fontSize="sm" fontWeight="600" color={textPrimary}>{firma.puesto}</Text>
                          {firmado && <Badge colorScheme="green" fontSize="10px" variant="subtle">Firmado</Badge>}
                          {!firmado && idx === firmasSalidaView.findIndex(f => Number(f.firmo) === 0) && (
                            <Badge colorScheme="purple" fontSize="10px" variant="subtle">Pendiente</Badge>
                          )}
                        </HStack>
                        {(firma.FullName || firma.nombre) && (
                          <Text fontSize="xs" color={textSecondary}>{firma.FullName || firma.nombre}</Text>
                        )}
                      </Box>
                      <Box w="10px" h="10px" borderRadius="full" mt={1}
                        bg={firmado ? 'green.400' : 'gray.300'} />
                    </Flex>
                    <Box h="90px" border="1px solid" borderColor={firmado ? 'green.100' : borderColor}
                      borderRadius="8px" bg="white" overflow="hidden"
                      display="flex" alignItems="center" justifyContent="center">
                      {firma.firma_base ? (
                        <Image src={firma.firma_base} alt={`Firma ${firma.puesto}`}
                          objectFit="contain" w="100%" h="90px" />
                      ) : (
                        <VStack spacing={1}>
                          <Icon as={MdAccessTime} w="18px" h="18px" color="gray.300" />
                          <Text fontSize="xs" color="gray.400">Pendiente de firma</Text>
                        </VStack>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </CardBody>
    </Card>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA BAJA
  // ════════════════════════════════════════════════════════════════════════════

  const renderVistaBaja = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader(
        mostrarSoloFirmas ? 'Firmas — Proceso de baja' : 'Baja definitiva',
        `Requiere 4 autorizaciones · ${selectedActivos.length} activo(s)`,
        'red.400',
        sessionStorage.getItem('idBajas') ? (
          <Button
            size="sm" variant="outline" colorScheme="red"
            leftIcon={<Icon as={MdDelete} />}
            onClick={handleEliminarProcesoBaja}
            flexShrink={0}
            isDisabled={isSoloLectura()}
          >
            Cancelar proceso
          </Button>
        ) : undefined
      )}

      {/* Stepper — solo en flujo normal */}
      {!mostrarSoloFirmas && (
        <Box px={6} py={4} borderBottom="1px solid" borderColor={borderColor}>
          <Stepper index={pasoActual} colorScheme="blue" size="sm">
            {pasosBaja.map((paso, i) => (
              <Step key={i}>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <Box flexShrink={0}><StepTitle>{paso}</StepTitle></Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <CardBody px={6} py={6}>

        {/* ── MODO SOLO FIRMAS ────────────────────────────────────────────────── */}
        {mostrarSoloFirmas ? (
          <Box>
            <Text {...labelStyle} mb={3}>Activos en proceso</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={6}>
              {selectedActivosObjects.map(activo => (
                <Box key={activo.id} p={3} border="1px solid" borderColor={borderColor}
                  borderRadius="10px" bg={cardBg}>
                  <HStack align="start">
                    <Image src={activo.URL_Imagen || getDefaultImage(activo.tipo)} alt={activo.nombre}
                      w="32px" h="32px" borderRadius="6px" objectFit="cover" flexShrink={0} />
                    <Box flex={1} overflow="hidden">
                      <Text fontSize="sm" fontWeight="600" color={textPrimary} isTruncated>{activo.nombre}</Text>
                      <Text fontSize="xs" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                    </Box>
                  </HStack>
                  {getMotivoActivo(activo.id) && (
                    <Text fontSize="xs" color={textSecondary} mt={2} pl={2}
                      borderLeft="2px solid" borderLeftColor="red.300">
                      {getMotivoActivo(activo.id)}
                    </Text>
                  )}
                </Box>
              ))}
            </SimpleGrid>

            <Text {...labelStyle} mb={4}>Autorizaciones</Text>
            {renderFirmas()}

            {mostrarSoloFirmas && usuarioCreadorCoincide() && (
              <Flex justify="flex-end" mt={6}>
                <Button colorScheme="red" onClick={handleProcesarBaja}
                  leftIcon={<Icon as={MdCheckCircle} />}
                  isDisabled={!todasLasFirmasCompletas() || isSoloLectura()}>
                  Procesar baja definitiva
                </Button>
              </Flex>
            )}
          </Box>

        ) : (
          <>
            {/* ── PASO 0: REVISIÓN ─────────────────────────────────────────────── */}
            {pasoActual === 0 && (
              <Box>
                <Text {...labelStyle} mb={3}>Activos seleccionados</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} maxH="420px" overflowY="auto" pr={1}>
                  {selectedActivosObjects.map(activo => (
                    <Box key={activo.id} p={4} border="1px solid" borderColor={borderColor}
                      borderRadius="10px" bg={cardBg}>
                      <HStack>
                        <Image src={activo.URL_Imagen || getDefaultImage(activo.tipo)} alt={activo.nombre}
                          w="40px" h="40px" borderRadius="8px" objectFit="cover" />
                        <Box flex={1}>
                          <Text fontWeight="600" fontSize="sm" color={textPrimary}>{activo.nombre}</Text>
                          <HStack spacing={2} mt={1}>
                            <Text fontSize="xs" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                            <Badge colorScheme={getEstadoColor(activo.estado)} fontSize="10px" variant="subtle" textAlign="center">
                              {activo.estado}
                            </Badge>
                          </HStack>
                        </Box>
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* ── PASO 1: MOTIVOS ───────────────────────────────────────────────── */}
            {pasoActual === 1 && (
              <Box>
                <Text {...labelStyle} mb={3}>Motivo por activo</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} maxH="360px" overflowY="auto" pr={1} mb={5}>
                  {selectedActivosObjects.map(activo => {
                    const tieneMotivo = getMotivoActivo(activo.id) !== '';
                    return (
                      <Box key={activo.id} p={4} border="1px solid" borderColor={borderColor}
                        borderRadius="10px" bg={cardBg}
                        borderLeft="3px solid" borderLeftColor={tieneMotivo ? 'red.400' : 'gray.200'}
                        transition="border-color 0.2s"
                      >
                        <HStack align="start" mb={3}>
                          <Image src={activo.URL_Imagen || getDefaultImage(activo.tipo)} alt={activo.nombre}
                            w="32px" h="32px" borderRadius="6px" objectFit="cover" flexShrink={0} />
                          <Box flex={1} overflow="hidden">
                            <Text fontWeight="600" fontSize="sm" color={textPrimary} isTruncated>{activo.nombre}</Text>
                            <Text fontSize="xs" color={textSecondary} fontFamily="mono">{activo.folio}</Text>
                          </Box>
                        </HStack>
                        <Textarea
                          value={getMotivoActivo(activo.id)}
                          onChange={e => updateMotivoActivo(activo.id, e.target.value)}
                          placeholder="¿Por qué se da de baja?"
                          size="sm" minH="64px" fontSize="sm" borderRadius="8px"
                          border="1px solid" borderColor={borderColor}
                          _focus={{ borderColor: 'red.400', boxShadow: 'none' }}
                        />
                      </Box>
                    );
                  })}
                </SimpleGrid>

                <Box mt={4} p={4} border="1px solid" borderColor={borderColor} borderRadius="10px">
                  <FormLabel fontSize="sm" fontWeight="600" color={textPrimary} mb={2}>
                    Motivo general *
                  </FormLabel>
                  <Textarea name="motivo" value={formData.motivo} onChange={handleInputChange}
                    placeholder="Describe el motivo general que justifica la baja de todos los activos..."
                    minH="90px" fontSize="sm" borderRadius="8px"
                    border="1px solid" borderColor={borderColor}
                    _focus={{ borderColor: 'red.400', boxShadow: 'none' }} />
                </Box>

                {/* Evidencia fotográfica */}
                <Box mt={4}>
                  <input
                    ref={fileInputBajaRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleFotosBajaAdded}
                  />
                  <input
                    ref={cameraInputBajaRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={handleFotosBajaAdded}
                  />

                  {/* Encabezado */}
                  <Flex align="center" gap={2} mb={4}>
                    <Box w="3px" h="18px" bg="red.400" borderRadius="full" flexShrink={0} />
                    <Text fontSize="sm" fontWeight="700" color={textPrimary}>Evidencia fotográfica</Text>
                    <Text fontSize="sm" fontWeight="700" color="red.400">*</Text>
                    {fotosBaja.length > 0 && (
                      <Badge colorScheme="red" variant="subtle" fontSize="10px" ml="auto">
                        {fotosBaja.length} foto{fotosBaja.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </Flex>

                  {fotosBaja.length === 0 ? (
                    /* Estado vacío */
                    <Box
                      border="2px dashed" borderColor="red.200" borderRadius="12px"
                      p={{ base: 5, md: 6 }} textAlign="center" cursor="pointer"
                      _hover={{ borderColor: 'red.400', bg: rowHover }}
                      transition="all 0.15s"
                      onClick={() => fileInputBajaRef.current?.click()}
                    >
                      <Icon as={MdPhotoCamera} boxSize={9} color="red.300" mb={3} />
                      <Text fontSize="sm" fontWeight="600" color={textSecondary} mb={1}>
                        Sin evidencia adjunta
                      </Text>
                      <Text fontSize="xs" color={textSecondary} mb={5}>
                        Se requiere al menos una foto del activo como evidencia
                      </Text>
                      <Flex
                        direction={{ base: 'column', sm: 'row' }}
                        justify="center" gap={3}
                      >
                        <Button
                          size="md" leftIcon={<Icon as={MdPhotoCamera} />}
                          colorScheme="red" variant="solid" w={{ base: 'full', sm: 'auto' }}
                          onClick={e => { e.stopPropagation(); cameraInputBajaRef.current?.click(); }}
                        >
                          Tomar foto
                        </Button>
                        <Button
                          size="md" leftIcon={<Icon as={MdImage} />}
                          colorScheme="red" variant="outline" w={{ base: 'full', sm: 'auto' }}
                          onClick={e => e.stopPropagation()}
                        >
                          Elegir de galería
                        </Button>
                      </Flex>
                    </Box>
                  ) : (
                    /* Grid de miniaturas */
                    <Box p={4} border="1px solid" borderColor={borderColor} borderRadius="12px" bg={cardBg}>
                      <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3} mb={4}>
                        {fotosBaja.map((foto, i) => (
                          <Box key={i} position="relative" borderRadius="10px" overflow="hidden"
                            border="1px solid" borderColor={borderColor}>
                            <Image src={foto.preview} alt={foto.name}
                              w="100%" h={{ base: '90px', md: '80px' }}
                              objectFit="cover" display="block" />
                            {/* Botón eliminar — siempre visible (touch-friendly) */}
                            <Box
                              position="absolute" top="6px" right="6px"
                              bg="red.500" borderRadius="full"
                              w="28px" h="28px" cursor="pointer"
                              display="flex" alignItems="center" justifyContent="center"
                              _hover={{ bg: 'red.600' }} boxShadow="md"
                              onClick={() => handleEliminarFotoBaja(i)}
                            >
                              <Icon as={MdClose} boxSize="13px" color="white" />
                            </Box>
                            <Box px={2} py={1} bg={cardBg}>
                              <Text fontSize="10px" color={textSecondary} isTruncated>{foto.name}</Text>
                            </Box>
                          </Box>
                        ))}
                      </SimpleGrid>

                      <Flex pt={3} borderTop="1px solid" borderColor={borderColor}
                        gap={3} direction={{ base: 'column', sm: 'row' }}>
                        <Button size="sm" leftIcon={<Icon as={MdPhotoCamera} />}
                          variant="outline" colorScheme="red" flex={1}
                          onClick={() => cameraInputBajaRef.current?.click()}>
                          Tomar foto
                        </Button>
                        <Button size="sm" leftIcon={<Icon as={MdImage} />}
                          variant="outline" colorScheme="gray" flex={1}
                          onClick={() => fileInputBajaRef.current?.click()}>
                          Agregar más
                        </Button>
                      </Flex>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* ── PASO 2: FIRMAS ────────────────────────────────────────────────── */}
            {pasoActual === 2 && (
              <Box>
                {/* Selector de flujo */}
                {flujosConfig.some(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0) && (
                  <Box mb={5} p={3} border="1px solid" borderColor={borderColor} borderRadius="10px" bg={cardBg}>
                    <Text fontSize="10px" fontWeight="700" color={textSecondary} textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Flujo de firmas
                    </Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {flujosConfig.filter(f => f.id.startsWith('dept-') ? f.id === `dept-${usuario?.DepartamentoId}` : f.pasos.length > 0).map(flujo => (
                        <Button key={flujo.id} size="xs"
                          variant={flujoAplicadoId === flujo.id ? 'solid' : 'outline'}
                          colorScheme={flujoAplicadoId === flujo.id ? 'red' : 'gray'}
                          onClick={async () => {
                            const solicitudId = sessionStorage.getItem('idBajas');
                            if (!solicitudId) return;
                            const user = JSON.parse(sessionStorage.getItem('user') || '{}');
                            const fId = flujo.id.startsWith('dept-') ? null : flujo.id;
                            await fetch('https://inventariosbackend.velasresorts.com/inventarios/bajas/cambiar-flujo', {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ solicitudId: Number(solicitudId), flujoId: fId, departamentoId: user.DepartamentoId, usuarioId: user.Id, hotel: hotelMap[hotelSeleccionado] || 1 }),
                            });
                            setFlujoAplicadoId(flujo.id);
                            await fetchFirmas();
                          }}
                        >
                          {flujo.name} · {flujo.pasos.length} firmantes
                        </Button>
                      ))}
                    </Flex>
                  </Box>
                )}

                {/* Resumen compacto */}
                <Box p={3} border="1px solid" borderColor={borderColor} borderRadius="10px"
                  borderLeft="3px solid" borderLeftColor="red.400" mb={5}>
                  <HStack spacing={6}>
                    <Box>
                      <Text {...labelStyle}>Activos</Text>
                      <Text fontSize="sm" fontWeight="700" color={textPrimary}>{selectedActivos.length}</Text>
                    </Box>
                    <Box>
                      <Text {...labelStyle}>Firmas</Text>
                      <Text fontSize="sm" fontWeight="700" color={textPrimary}>
                        {firmas.filter(f => Number(f.firmo) === 1).length}/{firmas.length}
                      </Text>
                    </Box>
                    <Box flex={1} overflow="hidden">
                      <Text {...labelStyle}>Motivo</Text>
                      <Text fontSize="sm" color={textPrimary} isTruncated>
                        {formData.motivo || '—'}
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                <Text {...labelStyle} mb={4}>Autorizaciones</Text>
                {renderFirmas()}

                {usuarioCreadorCoincide() && (
                  <Flex justify="flex-end" mt={6}>
                    <Button colorScheme="red" onClick={handleProcesarBaja}
                      leftIcon={<Icon as={MdCheckCircle} />}
                      isDisabled={!validarProcesoCompleto() || !todasLasFirmasCompletas() || isSoloLectura()}>
                      Procesar baja definitiva
                    </Button>
                  </Flex>
                )}
              </Box>
            )}
          </>
        )}
      </CardBody>

      {/* Footer de navegación — solo en flujo normal */}
      {!mostrarSoloFirmas && (
        <CardFooter borderTop="1px solid" borderColor={borderColor} px={6} py={4}>
          <Flex justify="space-between" align="center" w="100%">
            <Button variant="ghost" color={textSecondary}
              onClick={() => pasoActual > 0 ? setPasoActual(pasoActual - 1) : handleVolverALista()}
              leftIcon={<Icon as={MdArrowBack} />}
              _hover={{ bg: rowHover }}
            >
              {pasoActual === 0 ? 'Volver' : 'Anterior'}
            </Button>

            {pasoActual < 2 && (
              <Button colorScheme="blue" onClick={validarYAvanzarPaso}
                rightIcon={<Icon as={MdArrowForward} />}>
                Siguiente
              </Button>
            )}
          </Flex>
        </CardFooter>
      )}
    </Card>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // VISTA VER SALIDAS TEMPORALES
  // ════════════════════════════════════════════════════════════════════════════

  const renderVerSalidasTemporales = () => {
    const fmt = (d: string) => {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    };

    return (
      <Card p="0px" overflow="hidden">
        {renderFormHeader('Salidas temporales', 'Historial de pases de salida generados', 'purple.400')}

        <CardBody px={6} py={6}>
          {loadingSalidasTemporales ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Spinner size="lg" color="purple.400" thickness="3px" />
                <Text fontSize="sm" color={textSecondary}>Cargando salidas...</Text>
              </VStack>
            </Center>
          ) : salidasTemporales.length === 0 ? (
            <Center py={14}>
              <VStack spacing={3}>
                <Icon as={MdList} w="40px" h="40px" color="gray.300" />
                <Text fontSize="sm" color={textSecondary}>No hay salidas temporales registradas para este hotel</Text>
              </VStack>
            </Center>
          ) : (
            <VStack spacing={3} align="stretch">
              {salidasTemporales.map((salida: any) => {
                const firmasCompletas = Number(salida.firmas_completadas) === Number(salida.total_firmas) && Number(salida.total_firmas) > 0;
                return (
                  <Box key={salida.id} p={4} border="1px solid"
                    borderColor={firmasCompletas ? 'green.200' : borderColor}
                    borderRadius="12px" bg={cardBg}
                    borderLeft="3px solid"
                    borderLeftColor={firmasCompletas ? 'green.400' : 'orange.300'}
                  >
                    <Flex justify="space-between" align="start" wrap="wrap" gap={3}>
                      <Box flex={1} minW="200px">
                        <HStack spacing={2} mb={1} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="700" color="orange.500">{salida.folio}</Text>
                          <Badge
                            colorScheme={firmasCompletas ? 'green' : 'orange'}
                            fontSize="10px" variant="subtle">
                            {firmasCompletas ? 'Autorizado' : `${salida.firmas_completadas ?? 0}/${salida.total_firmas ?? 0} firmas`}
                          </Badge>
                        </HStack>
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={2}>
                          <Box>
                            <Text {...labelStyle}>Destinatario</Text>
                            <Text fontSize="xs" fontWeight="600" color={textPrimary}>{salida.usuario_destino || '—'}</Text>
                          </Box>
                          <Box>
                            <Text {...labelStyle}>Fecha salida</Text>
                            <Text fontSize="xs" fontWeight="600" color={textPrimary}>{fmt(salida.fecha_salida)}</Text>
                          </Box>
                          <Box>
                            <Text {...labelStyle}>Compromiso devolución</Text>
                            <Text fontSize="xs" fontWeight="600" color="orange.500">{fmt(salida.fecha_retorno)}</Text>
                          </Box>
                          <Box>
                            <Text {...labelStyle}>Activos</Text>
                            <Text fontSize="xs" fontWeight="600" color={textPrimary}>{salida.total_activos ?? '—'}</Text>
                          </Box>
                        </SimpleGrid>
                        {salida.motivo && (
                          <Text fontSize="xs" color={textSecondary} mt={2} noOfLines={1}>{salida.motivo}</Text>
                        )}
                      </Box>
                      <Button size="sm" colorScheme="orange" variant="outline"
                        leftIcon={<Icon as={MdPictureAsPdf} />}
                        isLoading={descargandoPdf}
                        onClick={() => handleDescargarPdf(salida.id, salida.folio)}>
                        PDF
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </VStack>
          )}
        </CardBody>
      </Card>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER PRINCIPAL
  // ════════════════════════════════════════════════════════════════════════════

  const viewVariants = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -10, transition: { duration: 0.18, ease: 'easeIn' } },
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 6 }} overflow="hidden">
      <div ref={pageTopRef} />
      <AnimatePresence mode="wait">
        {vistaActual === 'lista' && (
          <motion.div key="lista" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaLista()}
          </motion.div>
        )}
        {vistaActual === 'baja' && (
          <motion.div key="baja" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaBaja()}
          </motion.div>
        )}
        {vistaActual === 'salida' && (
          <motion.div key="salida" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaSalida()}
          </motion.div>
        )}
        {vistaActual === 'salida-temporal' && (
          <motion.div key="salida-temporal" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaSalidaTemporal()}
          </motion.div>
        )}
        {vistaActual === 'transferencia-firmas' && (
          <motion.div key="transferencia-firmas" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderFirmasTransferencia()}
          </motion.div>
        )}
        {vistaActual === 'ver-salida' && (
          <motion.div key="ver-salida" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVerSalida()}
          </motion.div>
        )}
        {vistaActual === 'ver-salidas-temporales' && (
          <motion.div key="ver-salidas-temporales" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVerSalidasTemporales()}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default SalidasPage;
